import { i18n } from '../i18n.js';
import { errorCode, playerFacingError } from './player-error.js';
import { getCloudAchievementBannerConsumer } from '../app-services.js';
import { MoonfallClickGuard } from '../utils/click-guard.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';
import { MoonfallCloudGuard } from '../utils/cloud-mutation-guard.js';
import { MoonfallCloud } from '../convex-client.js';
import { RegionAvailability } from '../region-availability.js';
import { AssetPreloader } from './asset-preloader.js';
import { CardbackArt } from './cardback-art.js';
import { CardBackRaster } from './card-back-raster.js';
import { CardPacksData } from './card-packs-data.js';
import { createPackExchangeSession } from './card-pack-exchange-session.js';
import { readPackOpeningCards } from './card-pack-opening-cards.js';
import { CardPacksOdds } from './card-packs-odds.js';
import { Cards } from './cards.js';
import { CardTilt } from './card-tilt.js';
import { Confirm } from './confirm-dialog.js';
import { FX } from './fx.js';
import { SoundEffects } from './sound-effects.js';
import { Stardust } from './stardust.js';
import { StellarAssets } from './stellar-assets.js';
import { TouchPreview } from './touch-preview.js';
import { uiCssPx, uiPx } from './ui-scale.js';
let CardPacks = {};


CardPacks = (function () {
  const TORN_PACK_SESSION_KEY = 'moonfall.cardPacks.tornPackId';

  function readSessionTornPackRecord() {
    try {
      const raw = window.sessionStorage?.getItem(TORN_PACK_SESSION_KEY);
      if (!raw) return null;
      const record = JSON.parse(raw);
      return typeof record?.packId === 'string'
        && record.packId
        && typeof record?.userId === 'string'
        && record.userId
        ? { packId: record.packId, userId: record.userId }
        : null;
    } catch (_) {
      return null;
    }
  }

  function storeSessionTornPackId(packId) {
    try {
      if (packId && currentUserId) {
        sessionTornPackRecord = {
          packId: String(packId),
          userId: String(currentUserId),
        };
        window.sessionStorage?.setItem(
          TORN_PACK_SESSION_KEY,
          JSON.stringify(sessionTornPackRecord),
        );
      } else {
        sessionTornPackRecord = null;
        window.sessionStorage?.removeItem(TORN_PACK_SESSION_KEY);
      }
    } catch (_) {
      // Storage can be unavailable in hardened/private browser contexts. The
      // in-memory latch and authoritative server write still protect the live
      // ceremony in those contexts.
    }
  }

  let allCards = [];
  let contextCards = [];
  let getLegacyCards = null;
  let openingCardVersion = 0;
  let ownedCardMap = new Map();
  let ownedCardStellarMap = new Map();
  let currentUserId = null;
  let stardust = 0;
  let packCount = 0;
  let activePack = null;
  let packConfig = { packPrice: 100, packSize: 5, stellarOdds: 0.02,
    stellarPackOdds: /** @type {number | undefined} */ (undefined) };
  // `packConfig` is SEEDED, not known. Its odds are a guess for the render
  // path; the published disclosure may only ever show a number the server
  // actually sent, so nothing is printed until this flips.
  let stellarOddsKnown = false;
  // Set only by the server's own report that this account's Stellar rate is an
  // override, never inferred from the port's request.
  let stellarDebugActive = false;
  let nonCraftableDuplicateValue = 0;
  let duplicateValuePerPack = 20;
  let normalDuplicateValue = 1;
  let stellarDuplicateValue = 20;
  let isLoading = false;
  let isOpening = false;
  let isConvertingDuplicates = false;
  let isSurfaceOpen = false;
  // Packs are off in some countries (Brazil). The server refuses there either
  // way; this lets the vault say so instead of offering a dead purchase.
  let packsUnavailableInRegion = false;
  let packRegionRead = null;
  // A null activePack is meaningful only after the authoritative state read.
  // Until then, the default DOM wrapper must not masquerade as a sealed pack.
  let packStateResolved = false;
  let onStardustUpdate = null;
  let onInventoryUpdate = null;
  let onInventoryInvalidated = null;
  let exchangeVersion = 0;
  let exchangeSession = null;
  let exchangeState = null;
  // What the session's phases mean for the rest of the vault. Only a running
  // exchange (or its recovery) owns it; a quote is bookkeeping the session
  // repeats on every entry, refresh and claim.
  const EXCHANGE_RUNNING = ['exchanging', 'reconnecting'];
  const EXCHANGE_QUOTING = ['loading', 'preparing'];
  const EXCHANGE_IDLE = ['ready', 'completed', 'interrupted', 'quote-changed'];
  // The session's last answer on this visit (any phase but a quote). It
  // survives the re-quote, so until the new answer lands the vault keeps
  // looking and behaving as the player already saw it.
  let exchangeAnswer = null;
  // Card taps made before this visit's session answered whether an exchange
  // is running, held on their cards (index -> tap) instead of dropped.
  const heldClaims = new Map();
  // Exchange presses waiting for the session's current quote, and whether the
  // wait has outlasted the grace (the pressed control then says so).
  const exchangeWaiters = new Set();
  let exchangePressWaiting = false;
  // Pack-changing actions still in flight, and whether a refresh they
  // overtook still has to read again once they settle.
  let unsettledMutations = 0;
  let packRefreshOwed = false;
  let screenReadController = null;
  let dragState = null;
  let i18nListenersReady = false;
  let openGeneration = 0;
  // View generations stop older section entries from repainting newer ones.
  // The state epoch independently fences server reads across authoritative
  // mutations, while the context generation prevents an old account's late
  // result from being projected into a newly selected account.
  let packStateEpoch = 0;
  let contextGeneration = 0;
  let equippedCardback = {
    kind: 'default',
    slug: 'default',
    url: '/assets/images/cardback.webp',
  };
  let cardbackObserver = null;
  const pendingClaims = new Set();
  const pendingOpenedMarks = new Set();
  const pendingRevealPreparations = new WeakMap();
  const renderedPackFaces = new WeakMap();
  // A revealed card is a printed object: it turns over onto a finished face or
  // it waits, visibly charging, for one (docs/asset-loading.md, the pack flip).
  // The art has been warming since the charge, so the wait is normally zero;
  // the cap is the ticket/cardback cap for a static-art object shown whole, and
  // past it the owned card turns text-first rather than stay face-down.
  const PACK_FLIP_HOLD_CAP_MS = 4000;
  // Bumped by every pack-changing action. A screen read taken before one of
  // them describes a vault that no longer exists and is never painted.
  let packMutationEpoch = 0;
  // The Packs tab's read, started on intent before the tab is shown (the
  // Collection opening, a pointer or focus on the tab). Consumed by at most one
  // entry; see warm()/prepare().
  const SCREEN_WARM_FRESH_MS = 60000;
  let screenWarm = null;
  // The warm read prepare() painted the tab from, for the entry about to
  // consume it: that entry takes nothing back (open()).
  let preparedWarm = null;
  // Whether `nonCraftableDuplicateValue` is a server total for this account
  // (the maintained total in the screen state, or the exchange session's own
  // quote) rather than the zero it starts at.
  let duplicateTotalKnown = false;
  // Who wrote the status line. Exchange bookkeeping may clear its own words,
  // never the ceremony's.
  let statusOwner = null;
  // Match the shared card-art loading grace. A loader may retry its real
  // identity indefinitely, but an inspection preview must stay usable.
  const PACK_ART_GRACE_MS = 450;
  async function withPackArtGrace(work) {
    let timer;
    try {
      return await Promise.race([
        Promise.resolve(work).catch(() => false),
        new Promise(resolve => { timer = setTimeout(() => resolve(null), PACK_ART_GRACE_MS); }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  }
  /* Accepted rewards are armed by STABLE identity (`packId:index`), never by
     the physical wrapper node: the claim itself schedules a re-render, and
     whenever that render REBUILT the wrapper instead of reusing it, a cue
     keyed on the old node vanished with it — the reveal flipped silently
     ("no card reveal sound. at least not reliably"). The generation and pack
     fields on each cue still refuse replay onto a newer or different pack,
     and every pack open/close clears the map outright. */
  const pendingRevealCues = new Map();
  // Mouse hover listeners are safe on a face-down card, but TouchPreview owns
  // the first tap on every attached element. Keep its pack-specific callbacks
  // here and attach them only after an accepted claim has exposed a real face.
  const revealedTouchPreviewOptions = new WeakMap();
  let spreadRaf = null;
  let fanGeometryObserver = null;
  // The live foil-debris container and the timer that reaps it.
  let foilBurstEl = null;
  let foilBurstTimer = null;
  let packTearTimer = null;
  let stageTearTimer = null;
  let sealNudgeTimer = null;
  // True only between a live tear and the fan it produced. A pack restored on
  // reload has no tear to wait for, so it must deal immediately.
  let tearLeadInPending = false;
  // The travel at which the wrapper starts to shiver — the last of the pull,
  // where the material is visibly about to give.
  const SEAL_STRAIN_PROGRESS = 0.72;
  // How long the tear gets to itself before the first card is dealt over it.
  // Only applies when the pack is torn in front of the player: a fan restored
  // on reload has no tear to wait for and must not sit blank.
  const FAN_LEAD_IN_MS = 170;
  // The pack this session tore open, held until the server's own `openedAt`
  // takes over. Keyed by pack id so it can never leak onto the next pack.
  let sessionTornPackRecord = readSessionTornPackRecord();
  let locallyTornPackId = null;
  const PACK_HOVER_PREVIEW_ENABLED = true;
  // Fan cards are PHYSICALLY sized (no transform zoom), so the raster only
  // needs headroom for fan rotation resampling and the popup transfer
  // (~2.1x the card's visual size).
  const PACK_CARD_RASTER_OVERSAMPLE = 2.2;
  const SEAL_DRAG_MIN_PX = 32;
  const SEAL_DRAG_MAX_PX = 120;
  const SEAL_DRAG_PACK_RATIO = 0.36;
  const COMPACT_PHONE_MEDIA_QUERY = '(orientation: portrait) and (max-width: 768px), (orientation: landscape) and (max-height: 560px)';

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    const text = translated && translated !== key ? translated : fallback;
    return Object.entries(replacements).reduce((result, [name, value]) => (
      result.replace(new RegExp(`\\{\\{${name}\\}\\}`, 'g'), String(value))
    ), String(text));
  }

  const formatStardust = (value) => Stardust?.format?.(value) ?? String(value);

  function applyPackTranslations() {
    if (i18n?.applyTranslations) {
      i18n.applyTranslations();
    }
  }

  function getSection() {
    return document.getElementById('collection-packs-section');
  }

  function isPackStateLoading() {
    return ['loading', 'error'].includes(getSection()?.dataset.packsLoadState);
  }

  function beginPackStateMutation() {
    packStateEpoch += 1;
    packMutationEpoch += 1;
    unsettledMutations += 1;
    return {
      contextGeneration,
      userId: currentUserId,
      finished: false,
    };
  }

  function ownsMutationContext(mutation) {
    return mutation?.contextGeneration === contextGeneration
      && mutation.userId === currentUserId;
  }

  function checkpointPackStateMutation(mutation) {
    if (ownsMutationContext(mutation)) packStateEpoch += 1;
  }

  function finishPackStateMutation(mutation) {
    if (!mutation || mutation.finished) return;
    mutation.finished = true;
    unsettledMutations = Math.max(0, unsettledMutations - 1);
    packMutationEpoch += 1;
    checkpointPackStateMutation(mutation);
    readOwedRefresh();
  }

  /**
   * A refresh overtaken by the player's own action, or by a running exchange
   * (open(), superseded), reads again once every action has settled: its
   * answer described the vault from before, but what it came for — current
   * terms, another device's changes, the new language's card descriptions —
   * is still owed. In a later task, so the action paints its own answer first.
   */
  function readOwedRefresh() {
    if (!packRefreshOwed || unsettledMutations > 0) return;
    packRefreshOwed = false;
    const owner = contextGeneration;
    setTimeout(() => {
      if (isSurfaceOpen && owner === contextGeneration && packStateResolved) void open();
    }, 0);
  }

  function bindGuarded(button, mutationKey, asyncHandler) {
    const cloudGuard = MoonfallCloudGuard;
    const clickGuard = MoonfallClickGuard;
    button.addEventListener('click', (event) => {
      const runner = () => Promise.resolve(asyncHandler(event));
      const guarded = cloudGuard ? () => cloudGuard.run(mutationKey, runner) : runner;
      if (clickGuard && typeof clickGuard.guard === 'function') {
        clickGuard.guard(button, guarded).catch((err) => console.error('[CardPacks] guarded action failed:', err))
          .finally(updateSummary);
      } else {
        guarded().catch((err) => console.error('[CardPacks] guarded action failed:', err));
      }
    });
  }

  function ensureLayout() {
    const section = getSection();
    if (!section || section.dataset.packsReady === 'true') return;

    section.dataset.packsReady = 'true';
    section.dataset.packsLoadState = 'idle';
    section.dataset.packsStateKnown = String(packStateResolved);
    section.innerHTML = `
      <div class="card-packs-shell">
        <!-- The vault's ONE loading state, and the vault's, not the stage's:
             inside the stage it resized with every line the header gained
             while the state was still being read. It shows only after the
             shared art grace, so a warmed entry never flashes it. -->
        <div class="card-packs-state-placeholder" role="status">
          <span class="card-packs-state-caption" data-i18n="collection.packs.loadingVault">Opening the vault…</span>
        </div>
        <div class="card-packs-header">
          <div class="card-packs-title">
            <div class="card-packs-eyebrow" data-i18n="collection.packs.eyebrow">Card Packs</div>
            <h3 data-i18n="collection.packs.title">Vault Sealed</h3>
            <!-- Seeded EMPTY. Its copy interpolates the server's pack size, so it
                 cannot carry a data-i18n attribute, and a hardcoded English
                 sentence in the markup would paint for a frame in every other
                 language. updateOdds() writes it as soon as the config lands. -->
            <p class="card-packs-subtitle" id="card-packs-subtitle"></p>
          </div>
          <!-- Deliberately a sibling of the decorative title block, not a child:
               short-landscape phones hide that whole block, and the published
               odds must outlive every such declutter. -->
          <p class="card-packs-odds" id="card-packs-odds" aria-live="polite"></p>
          <!-- Real-money equivalent for the Stardust price, at the decision
               point. Sibling of the odds line for the same reason: both are
               required purchase disclosures and must survive the short-landscape
               declutter that hides the decorative title block. -->
          <p class="card-packs-odds card-packs-price-note" id="card-packs-price-note" aria-live="polite"></p>
          <!-- Development only. Rendered solely from the server's own report
               that THIS account is running an overridden Stellar rate, so a
               debug port can never look like an ordinary one. Last, so it never
               sits between the two required purchase disclosures. -->
          <p class="card-packs-debug-odds" id="card-packs-debug-odds" hidden></p>
          <!-- Two controls that describe themselves, and the one legal line.
               The counters live ON their buttons: as separate chips they read
               as loose scattered text, and on a phone they drifted away from
               the number they qualify. The exchange RULES are a heads-up at
               press time, not a permanent paragraph. -->
          <div class="card-packs-header-actions">
            <div class="card-packs-count" id="card-packs-count">Packs: 0</div>
            <div class="card-packs-duplicates" id="card-packs-duplicates">
              <button type="button" class="btn deck-btn card-packs-duplicates-btn" id="card-packs-duplicates-btn" disabled>Exchange · 0/20</button>
            </div>
            <button type="button" class="btn deck-btn card-packs-buy-btn" id="card-packs-buy-btn" aria-describedby="card-packs-odds" data-i18n="collection.packs.buyPack">Buy pack</button>
          </div>
        </div>

        <div class="card-packs-stage" id="card-packs-stage">
          <button type="button" class="btn deck-btn card-packs-open-btn" id="card-packs-open-btn" data-i18n="collection.packs.chargePack">Charge pack</button>
          <div class="card-packs-pack" id="card-packs-pack">
            <div class="card-packs-pack-art" aria-hidden="true"></div>
            <div class="card-packs-pack-sheen" aria-hidden="true"></div>
            <div class="card-packs-pack-body">
              <button
                type="button"
                class="card-packs-pack-seal"
                id="card-packs-pack-seal"
                aria-describedby="card-packs-pack-hint"
                aria-label="Pull the ribbon downward to open, or press Enter or Space."
                data-i18n-aria-label="collection.packs.sealAriaLabel"
              >
                <span class="card-packs-pack-seal-text" data-i18n="collection.packs.drag">Pull</span>
              </button>
            </div>
          </div>
          <!-- The ground contact is the pack's SIBLING, never its child: as a
               child it rode the float transform, so the shadow rose with the
               object casting it and the levitation read as a wobble. Declared
               AFTER the pack so the state rules can reach it with a sibling
               combinator. -->
          <div class="card-packs-pack-glow"></div>
          <div class="card-packs-pack-hint" id="card-packs-pack-hint"></div>
          <div class="card-packs-cards" id="card-packs-cards"></div>
          <!-- The stage's one answer to the tear. It carries a pre-painted
               light and only its opacity animates, so the flare composites
               instead of re-blurring a full-width shadow every frame. -->
          <div class="card-packs-stage-flare" aria-hidden="true"></div>
        </div>

        <div class="card-packs-status" id="card-packs-status" role="status" aria-live="polite" aria-atomic="true"></div>
      </div>
    `;
    document.getElementById('card-packs-stage')
      ?.setAttribute('aria-busy', String(!packStateResolved));
    applyPackTranslations();
    updateDuplicatesUI();
  }

  function syncPackStateReadiness() {
    const section = getSection();
    const stage = document.getElementById('card-packs-stage');
    // Written only when it changes: a same-value write is still a mutation
    // every observer of the vault wakes for.
    const known = String(packStateResolved);
    if (section && section.dataset.packsStateKnown !== known) section.dataset.packsStateKnown = known;
    const busy = String(!packStateResolved);
    if (stage && stage.getAttribute('aria-busy') !== busy) stage.setAttribute('aria-busy', busy);
  }

  function setStatus(message, tone = 'info', owner = 'ceremony') {
    const statusEl = document.getElementById('card-packs-status');
    if (!statusEl) return;
    statusOwner = message ? owner : null;
    statusEl.textContent = message || '';
    statusEl.classList.remove('success', 'error');
    if (tone === 'success') statusEl.classList.add('success');
    if (tone === 'error') statusEl.classList.add('error');
  }

  function getStellarAsset(cardId) {
    if (!StellarAssets?.get) return null;
    return StellarAssets.get(cardId);
  }

  function buildCardElementData(card, variant = 'normal', stellarAsset = null) {
    const isStellar = variant === 'stellar';
    const data = {
      id: card.id,
      instanceId: card.id,
      name: card.name,
      type: card.type || 'Character',
      manaCost: card.cost || 0,
      picture: card.image || card.picture || card.id,
      imageUrl: card.image || card.picture,
      description: card.description || '',
      targetingType: card.targetingType || 'None',
      character: card.character,
      isStellar,
    };

    if (isStellar && stellarAsset?.status === 'complete') {
      StellarAssets?.applyMediaToCard?.(data, stellarAsset);
    }

    return data;
  }

  function applyInventoryDelta(cardId, variant, delta, refreshDuplicateValue = true) {
    if (!cardId || !Number.isFinite(delta)) return;
    const map = variant === 'stellar' ? ownedCardStellarMap : ownedCardMap;
    const current = Number(map.get(cardId) || 0);
    const nextQty = current + delta;
    if (nextQty > 0) {
      map.set(cardId, nextQty);
    } else {
      map.delete(cardId);
    }
    if (refreshDuplicateValue) updateDuplicateStateFromInventory();
    if (typeof onInventoryUpdate === 'function') {
      onInventoryUpdate(cardId, nextQty, variant);
    }
  }

  function calculateNonCraftableDuplicateValue() {
    if (!CardPacksData?.calculateNonCraftableDuplicateValue) return 0;
    return CardPacksData.calculateNonCraftableDuplicateValue({
      allCards,
      ownedCardMap,
      ownedCardStellarMap,
      normalDuplicateValue,
      stellarDuplicateValue,
    });
  }

  function updateDuplicateStateFromInventory() {
    if (exchangeVersion === 1) return;
    const nextTotal = calculateNonCraftableDuplicateValue();
    if (Number.isFinite(nextTotal)) {
      nonCraftableDuplicateValue = nextTotal;
    }
  }

  /**
   * A cardback `cardPacks:openPack` granted while charging THIS pack.
   *
   * The grant lands at charge time but the celebration belongs at the end of
   * the ceremony, where it does not compete with the tear and the reveals.
   * Held by pack id so a refresh onto a different pack cannot replay it, and
   * drained exactly once. Null for every pack after the first, which is why
   * the ordinary open still costs zero extra calls.
   */
  let pendingCardbackUnlockPackId = null;

  function drainPendingCardbackUnlock(packId) {
    if (!packId || pendingCardbackUnlockPackId !== packId) return;
    pendingCardbackUnlockPackId = null;
    void getCloudAchievementBannerConsumer()?.();
  }

  /**
   * The finished opening still on the stage, if any: every card of the fan the
   * player is looking at has been revealed. The server lists only pending
   * openings (convex/cardPacks.ts resolveActivePack), so any re-read after the
   * last reveal reports none — and coming back to the tab used to clear the
   * whole fan under the player, leaving "No packs in the vault." (the owner,
   * 28 September 2026: "when i came back cards disappeared"). A refresh keeps
   * it (open()); charging the next pack, another pending opening on the next
   * read, an account change or a new entry to the tab replaces it.
   */
  function finishedPackOnStage() {
    const pack = activePack;
    if (!pack?.id || !pack.opened || !Array.isArray(pack.items) || pack.items.length === 0
      || !pack.items.every((item) => item?.claimed === true)) return null;
    const container = document.getElementById('card-packs-cards');
    return container?.dataset.packId === String(pack.id) ? pack : null;
  }

  function setActivePack(pack) {
    if (!pack) {
      clearTearPresentation();
      activePack = null;
      locallyTornPackId = null;
      storeSessionTornPackId(null);
      return;
    }

    const items = Array.isArray(pack.items) ? pack.items : [];
    const id = pack.id || pack.packId;
    if (activePack?.id && activePack.id !== id) {
      clearTearPresentation();
    }
    const locallyTorn = Boolean(id) && id === locallyTornPackId;
    if (locallyTornPackId && id !== locallyTornPackId) {
      locallyTornPackId = null;
      storeSessionTornPackId(null);
    }
    activePack = {
      id,
      items,
      // Which collection the server rolled. Dropping it here is what made every
      // charged pack keep the original wrapper.
      collection: pack.collection || null,
      // Torn-open is REAL state, not something to infer from claims: a player
      // who pulled the ribbon and claimed nothing must not meet the sealed
      // wrapper again. `openedAt` is the server's record; the local latch
      // covers the window before that write lands (a stale refresh in between
      // used to flash the whole pack back over the tear animation).
      opened: Boolean(pack.openedAt)
        || items.some((item) => item.claimed)
        || locallyTorn,
      complete: pack.status === 'complete',
    };
    if (locallyTorn && !pack.openedAt) {
      // The session latch may have survived a navigation that aborted the
      // first network request. Reissue the idempotent authoritative write so
      // the repair becomes durable beyond this tab as well.
      rememberPackOpened(id);
    }
    if (activePack.complete && locallyTorn) {
      locallyTornPackId = null;
      storeSessionTornPackId(null);
    }
    prewarmKnownPackArt(items);
    // A charged pack will be torn; its backs are the first thing the player
    // sees afterwards, so decode them while the wrapper is still sealed.
    if (!activePack.opened) warmCardback();
  }

  function prewarmKnownPackArt(items) {
    const warmCards = AssetPreloader?.warmCards;
    if (typeof warmCards !== 'function' || !Array.isArray(items) || items.length === 0) return;
    const cardIds = new Set(items.map((item) => item?.cardId).filter(Boolean));
    const cards = allCards.filter((card) => cardIds.has(card?.id));
    if (cards.length === 0) return;
    try {
      const warm = warmCards(cards, { priority: 'high' });
      if (warm && typeof warm.then === 'function') {
        void Promise.resolve(warm).catch(() => undefined);
      }
    } catch (_) {
      // Warming is speculative. The strict connected reveal settlement below
      // remains the only authority allowed to expose a card face.
    }
  }

  function playSound(name, options) {
    if (!SoundEffects?.play || !name) return;
    try {
      // Only the cue that needs load-wait options carries a second argument.
      // Forwarding an explicit `undefined` for every other sound would change
      // the observable call signature of cues this fix never targeted.
      if (options === undefined) SoundEffects.play(name);
      else SoundEffects.play(name, options);
    } catch (err) {
      console.warn('[CardPacks] Failed to play sound:', err);
    }
  }

  /* The charge beat runs 1040ms end to end (gather 260 + bloom 460@220 +
     arrive 620@240 + sweep 520@520). The class is removed on the last
     animationend, with this as the fail-safe: a missed event must never leave
     the stage stuck mid-ceremony. */
  const PACK_CHARGE_BEAT_MS = 1200;
  let packChargeBeatTimer = 0;

  /**
   * Play the arrival. Presentation only — it observes a charge the server has
   * already granted and never gates, delays or synthesizes one, so a dropped
   * animation costs a flourish and nothing else.
   */
  function playPackChargeBeat() {
    const stage = document.getElementById('card-packs-stage');
    if (!stage) return;
    const pack = document.getElementById('card-packs-pack');
    clearTimeout(packChargeBeatTimer);
    stage.classList.remove('card-packs-charging');
    pack?.classList.remove('card-packs-pack-charging');
    // Reflow so a second charge restarts from the anticipation frame instead
    // of being ignored while the first is still settling.
    void stage.offsetWidth;
    /* The pack carries its own state class for the same reason dragging and
       tearing do: the phone tiers pin the idle float with a five-deep :not()
       chain that outranks any plain descendant selector, so without this the
       whole arrival is silently stilled on exactly the viewports the beat was
       asked for. */
    pack?.classList.add('card-packs-pack-charging');
    stage.classList.add('card-packs-charging');
    // Sound leads the visual: the transient lands with the bloom, not after it.
    playSound('impact');
    const stop = () => {
      clearTimeout(packChargeBeatTimer);
      stage.classList.remove('card-packs-charging');
      pack?.classList.remove('card-packs-pack-charging');
    };
    packChargeBeatTimer = setTimeout(stop, PACK_CHARGE_BEAT_MS);
  }


  async function loadPackStellarAssets(items) {
    if (!StellarAssets?.load) return;
    const ids = Array.from(new Set(
      (items || [])
        .filter((item) => item && item.isStellar)
        .map((item) => item.cardId)
        .filter(Boolean)
    ));
    if (ids.length === 0) return;
    try {
      await StellarAssets.load(ids);
    } catch (err) {
      console.warn('[CardPacks] Failed to load stellar assets:', err);
    }
  }

  /**
   * Print the published odds.
   *
   * A pack is a paid randomized purchase, so the Stellar chance belongs on
   * screen next to the buy button — the numbers come from the server's config
   * on every render, so what the player reads is what the server rolls.
   */
  /**
   * `#card-packs-odds` is an aria-live region and this runs after every buy,
   * charge, and claim. Writing textContent replaces the text node even when the
   * string is identical, which makes a screen reader read the odds aloud again
   * each time — so only write on a real change.
   */
  function setTextIfChanged(element, text) {
    if (element.textContent === text) return;
    element.textContent = text;
  }

  /**
   * `stellarText` is the SAME string the disclosure prints, so the badge can
   * never quote a different number than the one the server published. Passing
   * null hides the badge.
   */
  function updateDebugOddsBadge(stellarText) {
    const el = document.getElementById('card-packs-debug-odds');
    if (!el) return;
    const show = stellarDebugActive && Boolean(stellarText);
    el.hidden = !show;
    setTextIfChanged(el, show
      ? uiText(
        'collection.packs.debugOdds',
        'Debug port — this account rolls Stellar at {{percent}}.',
        { percent: stellarText }
      )
      : '');
  }

  /**
   * Show what the pack's Stardust price is worth in real money.
   *
   * Stardust is purchasable, so its spend price needs a real-money reference at
   * the decision point. The rate is derived from the live pack catalogue rather
   * than assumed, and until it arrives the line stays empty — quoting a guessed
   * price is worse than quoting none.
   */
  function updatePriceEquivalent() {
    const el = document.getElementById('card-packs-price-note');
    if (!el) return;
    const money = Stardust?.moneyEquivalent?.(packConfig.packPrice);
    if (!money) {
      setTextIfChanged(el, '');
      Stardust?.loadReferenceRate?.().then((rate) => {
        if (rate) updatePriceEquivalent();
      });
      return;
    }
    setTextIfChanged(el, uiText(
      'collection.packs.priceEquivalent',
      'Pack price: {{cost}} Stardust ({{money}})',
      { cost: formatStardust(packConfig.packPrice), money },
    ));
  }

  function updateOdds() {
    const subtitleEl = document.getElementById('card-packs-subtitle');
    const oddsEl = document.getElementById('card-packs-odds');
    if (!subtitleEl || !oddsEl) {
      updateDebugOddsBadge(null);
      return false;
    }

    // A config that never arrived leaves the seeded guess in place. Printing it
    // would be inventing a disclosure, so the line stays empty and the till
    // stays shut until the server has spoken.
    if (!stellarOddsKnown) {
      setTextIfChanged(oddsEl, '');
      updateDebugOddsBadge(null);
      return false;
    }

    const odds = CardPacksOdds?.describe({
      packSize: packConfig.packSize,
      stellarOdds: packConfig.stellarOdds,
      stellarPackOdds: packConfig.stellarPackOdds,
      locale: i18n?.getLanguage?.() || 'en',
    });
    if (!odds) {
      setTextIfChanged(oddsEl, '');
      updateDebugOddsBadge(null);
      return false;
    }
    updateDebugOddsBadge(odds.stellarText);

    // TWO LINES, EACH SAYING ONE THING. The odds were once stated twice — a
    // friendly per-PACK line on the decorative subtitle and the required
    // per-CARD disclosure here — and merging them into one sentence fixed the
    // repetition by making the surviving line say everything at once: eighty-two
    // characters of it, three lines deep in the phone rail's 202px lane, printed
    // across the Exchange button underneath. Owner's words: "it used to be
    // perfect, but redundant. Don't bloat it."
    //
    // So the split is back, with the repetition gone instead of merged. The
    // disclosure leads with the figure a player actually asks about — how often
    // a PACK wakes a Stellar — and carries the per-card rate and its complement
    // that the published odds require. The decorative line keeps only what the
    // pack holds, and nothing here depends on it: short-landscape phones hide
    // that whole block, and every figure still survives the declutter.
    setTextIfChanged(subtitleEl, uiText(
      'collection.packs.subtitle',
      '{{count}} cards out of the dark.',
      { count: packConfig.packSize },
    ));
    setTextIfChanged(oddsEl, odds.canDropStellar
      ? uiText(
        'collection.packs.odds',
        '{{atLeastOne}} of packs wake a Stellar — {{stellar}} per card, {{normal}} normal.',
        {
          atLeastOne: odds.atLeastOneText,
          stellar: odds.stellarText,
          normal: odds.normalText,
        }
      )
      : uiText(
        'collection.packs.oddsNoStellar',
        'No Stellar card can wake right now — {{stellar}} per card.',
        { stellar: odds.stellarText }
      ));
    return true;
  }

  function updateSummary() {
    const countEl = document.getElementById('card-packs-count');
    if (countEl) {
      countEl.textContent = uiText('collection.packs.packCount', 'Packs: {{count}}', {
        count: packCount,
      });
    }

    // A randomized paid purchase without its published odds on screen is not
    // one we are allowed to offer, so a disclosure that failed to render
    // closes the till rather than selling quietly.
    const oddsPublished = updateOdds();

    const buyBtn = document.getElementById('card-packs-buy-btn');
    if (buyBtn) {
      buyBtn.textContent = uiText('collection.packs.buyPackCost', 'Buy pack - {{cost}} Stardust', {
        cost: formatStardust(packConfig.packPrice),
      });
      buyBtn.dataset.countValue = String(packConfig.packPrice);
      updatePriceEquivalent();
      buyBtn.setAttribute('aria-label', uiText(
        'collection.packs.buyPackCost',
        'Buy pack - {{cost}} Stardust',
        { cost: packConfig.packPrice },
      ));
      buyBtn.disabled = isPackStateLoading()
        || isLoading
        || isConvertingDuplicates
        || !oddsPublished
        || packsUnavailableInRegion
        || stardust < packConfig.packPrice;
      // Affordable = alive: a soft pulse invites the purchase.
      buyBtn.classList.toggle('card-packs-buy-ready', !buyBtn.disabled);
    }

    const openBtn = document.getElementById('card-packs-open-btn');
    if (openBtn) {
      const hasBlockingPack = !!activePack && !activePack.complete;
      openBtn.disabled = isPackStateLoading()
        || isLoading
        || isOpening
        || isConvertingDuplicates
        || packsUnavailableInRegion
        || packCount <= 0
        || hasBlockingPack;
    }

    updateDuplicatesUI();
  }

  function updateDuplicatesUI() {
    const button = /** @type {HTMLButtonElement} */ (document.getElementById('card-packs-duplicates-btn'));
    if (!button) return;

    const perPack = Number.isFinite(duplicateValuePerPack) && duplicateValuePerPack > 0
      ? Math.floor(duplicateValuePerPack)
      : 20;
    const total = Number.isFinite(nonCraftableDuplicateValue)
      ? Math.max(0, Math.floor(nonCraftableDuplicateValue))
      : 0;
    const available = perPack > 0 ? Math.floor(total / perPack) : 0;
    const preparingLabel = uiText('collection.packs.exchangePreparing', 'Counting extra copies…');
    // The control is as wide as its widest transient label from the first
    // paint (`::before` in deck-builder.css), so no label it changes to moves
    // the "Packs: N" chip beside it. That swap measured 92px, twice per reveal.
    button.dataset.reserve = preparingLabel;

    /* The counter is part of the control. Short of a pack it reads as progress
       toward one ("Exchange · 7/20"); once it can be spent it names the reward
       instead, because at that point the ratio is no longer the useful fact. */
    if (available > 1) {
      button.textContent = uiText('collection.packs.convertPacks', 'Exchange for {{count}} packs', {
        count: available,
      });
    } else if (available === 1) {
      button.textContent = uiText('collection.packs.convertToPack', 'Exchange for 1 pack');
    } else {
      button.textContent = uiText('collection.packs.convertProgress', 'Exchange · {{count}}/{{required}}', {
        count: total,
        required: perPack,
      });
    }
    // A total the server already reported stays readable while the session
    // counts a fresh quote (on every entry and after every claim). Only an
    // account with no known total reads as counting — and a press that has
    // waited past the grace for that quote (handleConvertDuplicates).
    const quoting = exchangeVersion === 1 && !exchangeState?.quote?.ready;
    const counting = (quoting && !duplicateTotalKnown) || exchangePressWaiting;
    if (counting) {
      button.textContent = preparingLabel;
    }
    // Where packs are off the control keeps its count and is simply disabled.
    const errored = exchangeVersion === 1 && exchangeState?.phase === 'error' && !packsUnavailableInRegion;
    /* SHORT OF A PACK'S WORTH, THE CONTROL LOOKS LIKE WHAT IT IS (17 October
       2026). "Exchange · 0/20" — every new player's — cannot do anything; it
       is a progress readout, and it wore the same teal face, glow and pointer
       as "Exchange for 2 packs". It is drained for exactly that state
       (`--short`, deck-builder.css). A moment of the screen — a quote being
       counted, a purchase or an opening running — keeps the look the player
       already saw, as below: greying the control for those blinked the
       brightest thing on the screen. */
    // Where packs are off it can do nothing either, so it wears the same face.
    button.classList.toggle('card-packs-duplicates-btn--short',
      (available <= 0 || packsUnavailableInRegion) && !counting && !errored);
    if (errored) {
      button.textContent = uiText('collection.cards.retry', 'Retry');
      button.disabled = isPackStateLoading();
      return;
    }
    /* A quote is bookkeeping, not an exchange. Found 25 September 2026 (flicker
       hunt): greying the control until the quote (or a claim) came back blinked
       the brightest control on the screen dim and back on every reveal and on
       every entry. It keeps the look the player already saw; a press waits for
       the current quote instead of being refused, so nothing is ever confirmed
       against an old one. */
    button.disabled = exchangePressWaiting || isPackStateLoading() || isConvertingDuplicates
      || isLoading || isOpening || packsUnavailableInRegion || available <= 0
      || (exchangeVersion === 1 ? quoting && !duplicateTotalKnown : pendingClaims.size > 0);
  }

  // The wrapper reveals which collection the sealed pack turned out to be.
  // Slug matches the generated asset filenames in assets/images/packs/.
  const COLLECTION_SLUGS = {
    'Classic': 'classic',
    'Mythos': 'mythos',
    'Anime Archetypes': 'anime-archetypes',
    'Public Domain': 'public-domain',
    'Cute Creatures': 'cute-creatures',
    'Fun': 'fun',
    'Brainrot': 'brainrot',
  };

  /* ---- wrapper art ---------------------------------------------------------
     Each collection has its own 360-610 KB wrapper, selected purely in CSS by
     `[data-pack-collection]`. The slug is only known once the server names the
     collection — i.e. at the charge moment — so setting it there is also the
     moment the fetch STARTS: measured, zero wrappers were requested ahead of
     time, and on a throttled connection the pack silhouette stood on stage with
     no art on it until the bytes landed. "the deck sillhouette appears but the
     asset is not loading instantly, looks buggy."

     Two halves, both from docs/asset-loading.md: warm the wrappers on surface
     entry (law 3 — best-effort, never blocking, never behaviour-changing), and
     hold the pack's ARRIVAL on its wrapper's decode (law 2) so it appears
     complete instead of arriving in pieces.

     Found 25 September 2026 (flicker hunt): that hold was dead. A wrapper
     counted as "warmed" when its request STARTED, and open() starts all of
     them, so a charged pack was always painted at once — on a phone's first
     visit, as an empty silhouette whose art popped in when the bytes landed.
     The fallback (swap the generic wrapper for the collection's once decoded)
     could not have run, and would have reshaped a visible pack: each wrapper
     pins its own aspect-ratio. The pack now waits, where the ceremony already
     waits for its metadata, for its own wrapper (holdForPackWrapper). */
  const wrapperWarms = new Map();

  /* The GENERIC wrapper is the placeholder every reveal above stands on -- the
     stage paints it (as `.card-packs-pack-art`'s CSS background) the moment a
     charged pack exists, and the tear's two halves are cut from it too. It was
     the one wrapper nothing warmed: a CSS background is requested only when
     its element first gets a box, so on a cold cache the first charged pack
     stood on stage as a drop-shadow with the hint and the buttons printed
     beside it while 110KB arrived. Same defect, one file earlier. */
  const GENERIC_WRAPPER = 'assets/images/card-pack-front.webp';

  function wrapperUrl(slug) {
    return `assets/images/packs/card-pack-${slug}.webp`;
  }

  /**
   * Start, or join, the one request for a wrapper picture. `high` promotes a
   * picture the ceremony is about to show.
   * @param {string} key @param {string} url @param {{ priority?: 'high' | 'low' | 'auto' }} [options]
   */
  function warmWrapperSource(key, url, { priority = 'low' } = {}) {
    if (!key) return null;
    let warm = wrapperWarms.get(key);
    if (warm) {
      if (priority === 'high') {
        try { warm.img.fetchPriority = 'high'; } catch (_) { /* not supported */ }
      }
      return warm;
    }
    const img = new Image();
    // Best-effort: a warm must never compete with the state read that decides
    // whether there is even a pack to open.
    try { img.fetchPriority = priority; } catch (_) { /* not supported */ }
    img.decoding = 'async';
    warm = { img, loaded: null, decoded: null };
    // Both outcomes settle: a missing file must never hold the ceremony.
    warm.loaded = new Promise((resolve) => {
      img.onload = () => resolve(undefined);
      img.onerror = () => resolve(undefined);
    });
    img.src = url;
    wrapperWarms.set(key, warm);
    return warm;
  }

  /* The picture every charged pack shows first (110 KB), ahead of the tab:
     on a phone's 4G the charge came before a warm started on entering the
     tab had landed, and the pack stood on stage for ~1.7 s without it
     (measured 25 September 2026). The collections' own wrappers still wait
     for the tab.

     NOT BESIDE THE PAGE ON SCREEN. Started with the Collection's open, it
     went on the wire 50 ms after the press, beside the page the player
     opened: on 1 Mbps that page's pictures then missed their 30 s attempt
     window (28 September 2026, the Collection's slow-link spec). The open
     passes its page's own pictures (`after`) and the picture follows them,
     whether they landed or failed; a hung one is ended by the art loader's
     own attempt deadline, never a short cap of this module's. On a fast
     link that is within a second of the open, long before a charge, and a
     pointer on the Packs tab (intent: no `after`) asks for it at once. */
  function warmGenericWrapper(after = null) {
    const start = () => { warmWrapperSource(GENERIC_WRAPPER, GENERIC_WRAPPER, { priority: 'auto' }); };
    if (after && typeof after.then === 'function') {
      after.then(start, start);
      return;
    }
    start();
  }

  function warmAllPackWrappers() {
    // At the priority the open's warm gives it: it is the tab's first picture.
    warmGenericWrapper();
    Object.values(COLLECTION_SLUGS).forEach((slug) => { warmWrapperSource(slug, wrapperUrl(slug)); });
  }

  /** Settles once the collection's wrapper is decoded, or failed to load. */
  function wrapperDecoded(slug) {
    const warm = warmWrapperSource(slug, wrapperUrl(slug), { priority: 'high' });
    if (!warm) return Promise.resolve();
    if (!warm.decoded) {
      warm.decoded = typeof warm.img.decode === 'function'
        ? warm.img.decode().then(() => undefined, () => undefined)
        : warm.loaded;
    }
    return warm.decoded;
  }

  /**
   * What a charged pack's arrival waits for, beside its metadata: the wrapper
   * it will wear, decoded, so it appears whole in one task. Capped by the flip
   * hold; past it the pack appears anyway in its own collection, which is the
   * truth (the art may still land). A torn pack shows no wrapper (the opened
   * state is invisible) and a pre-collection one wears the generic: neither
   * waits.
   * @param {{ id?: string, packId?: string, collection?: string, openedAt?: number, status?: string, items?: Array<{ claimed?: boolean }> } | null | undefined} pack
   */
  function holdForPackWrapper(pack) {
    const slug = COLLECTION_SLUGS[pack?.collection];
    const id = pack?.id || pack?.packId;
    const torn = Boolean(pack?.openedAt) || pack?.status === 'complete'
      || (Array.isArray(pack?.items) && pack.items.some((item) => item?.claimed))
      || (Boolean(id) && id === locallyTornPackId);
    if (!slug || torn) return null;
    return holdPackFlip(wrapperDecoded(slug));
  }

  function applyPackCollection() {
    const stage = document.getElementById('card-packs-stage');
    if (!stage) return;
    const slug = COLLECTION_SLUGS[activePack?.collection];
    // No slug = a pre-collection opening or no charged pack. The original
    // wrapper identity remains available for a future charged ceremony; the
    // vacant-stage policy decides whether that wrapper is painted now.
    if (!slug) {
      delete stage.dataset.packCollection;
      return;
    }
    // The pack only became active once this wrapper decoded, or its hold's
    // cap passed (holdForPackWrapper).
    stage.dataset.packCollection = slug;
  }

  function renderPackStage() {
    applyPackCollection();
    const packEl = document.getElementById('card-packs-pack');
    const sealEl = document.getElementById('card-packs-pack-seal');
    const hintEl = document.getElementById('card-packs-pack-hint');
    const stageEl = document.getElementById('card-packs-stage');
    const openBtn = document.getElementById('card-packs-open-btn');
    if (!packEl || !hintEl || !stageEl) return;

    resetSealDrag();
    // Toggle, never remove-then-re-add: stripping `-opened` while the tear is
    // still animating re-showed the whole wrapper for a frame and restarted
    // its animations.
    const isOpened = Boolean(activePack && (activePack.complete || activePack.opened));
    packEl.classList.toggle('card-packs-pack-opened', isOpened);
    stageEl.classList.toggle('card-packs-stage-opened', isOpened);
    // Inventory is not a physical pack on the stage. Until the server returns
    // an active (charged) pack, keep the ceremony vacant and let the explicit
    // hint/action describe what the player can do next. Retain the two inert
    // classes only for their distinct inventory semantics and gesture guards.
    const awaitingCharge = !activePack && packCount > 0;
    const emptyVault = !activePack && packCount <= 0;
    packEl.classList.toggle('card-packs-pack-disabled', emptyVault);
    packEl.classList.toggle('card-packs-pack-uncharged', awaitingCharge);
    stageEl.classList.toggle('card-packs-stage-empty', emptyVault);
    stageEl.classList.toggle('card-packs-stage-vacant', !activePack);
    // The sealed pack needs its ground contact placed too, and every branch
    // below returns early — so this cannot live at the end of the function.
    scheduleSpreadUpdate();

    // On a vacant stage Charge keeps its corner: disabled with an empty vault
    // (updateSummary), never hidden. Hiding it there made it pop in half a
    // second after the purchase that enabled it. The inline value is REMOVED
    // rather than set, so the stylesheet's hold on a vault whose state is not
    // known yet still applies to it.
    if (!activePack) {
      if (sealEl) sealEl.disabled = true;
      hintEl.textContent = packsUnavailableInRegion
        ? uiText('collection.packs.unavailableInRegion', 'Card packs aren\'t available in your country.')
        : packCount > 0
          ? uiText('collection.packs.chargeHint', 'Charge a pack to begin.')
          : uiText('collection.packs.noPacks', 'No packs in the vault.');
      if (openBtn) {
        openBtn.textContent = uiText('collection.packs.chargePack', 'Charge pack');
        openBtn.style.removeProperty('visibility');
      }
      return;
    }

    if (activePack.complete) {
      if (sealEl) sealEl.disabled = true;
      hintEl.textContent = packsUnavailableInRegion
        ? uiText('collection.packs.unavailableInRegion', 'Card packs aren\'t available in your country.')
        : packCount > 0
          ? uiText('collection.packs.anotherPack', 'Another pack awaits.')
          : uiText('collection.packs.packComplete', 'Pack complete.');
      if (openBtn) {
        openBtn.textContent = uiText('collection.packs.chargePack', 'Charge pack');
        // Over a completed fan it appears only as the next action; a dead
        // control beside the cards would be clutter the fan does not need.
        if (packCount > 0 && !packsUnavailableInRegion) openBtn.style.removeProperty('visibility');
        else openBtn.style.visibility = 'hidden';
      }
      return;
    }

    if (activePack.opened) {
      if (sealEl) sealEl.disabled = true;
      hintEl.textContent = uiText('collection.packs.tapReveal', 'Tap a card to reveal it.');
      if (openBtn) openBtn.style.visibility = 'hidden';
      return;
    }

    if (sealEl) sealEl.disabled = false;
    hintEl.textContent = uiText('collection.packs.dragSeal', 'Pull the ribbon down to open.');
    if (openBtn) openBtn.style.visibility = 'hidden';
  }

  function renderPackCards() {
    const container = document.getElementById('card-packs-cards');
    if (!container || !CardPacksData) return;
    const preview = activePackPreview();
    if (preview && !preview.__packSource?.isConnected) {
      dismissPackPreview();
    }

    if (!activePack || !activePack.opened) {
      container.innerHTML = '';
      container.dataset.packId = '';
      return;
    }

    const view = CardPacksData.buildPackCardView({
      packItems: activePack.items,
      allCards,
    });

    const total = view.length;
    const center = (total - 1) / 2;
    container.style.setProperty('--card-total', String(total || 0));

    if (container.dataset.packId !== String(activePack.id || '')) {
      container.innerHTML = '';
      container.dataset.packId = String(activePack.id || '');
      const leadIn = tearLeadInPending ? FAN_LEAD_IN_MS : 0;
      tearLeadInPending = false;

      view.forEach((item, index) => {
        const offset = index - center;
        const tilt = offset * 4;
        const lift = Math.max(0, 18 - Math.abs(offset) * 3);
        const depth = total - Math.abs(offset);
        const cardWrapper = document.createElement('button');
        cardWrapper.type = 'button';
        cardWrapper.className = 'card-pack-card';
        // Owns its press cue: handleClaimCard ticks only for a claim it
        // actually attempts, so the shared press listener stays out.
        cardWrapper.dataset.pressSound = 'off';
        cardWrapper.dataset.index = String(index);
        cardWrapper.dataset.cardId = item.cardId;
        cardWrapper.setAttribute('aria-label', uiText(
          'collection.packs.revealCard',
          'Reveal card {{number}}: {{name}}',
          { number: index + 1, name: item.cardName }
        ));
        cardWrapper.style.setProperty('--card-offset', offset.toFixed(2));
        cardWrapper.style.setProperty('--card-tilt', `${tilt}deg`);
        cardWrapper.style.setProperty('--card-lift', uiCssPx(lift));
        // The deal waits for the rip to be SEEN. Frame captures showed the
        // first card arriving on the same frame as the flash, so the halves
        // shearing apart and the foil debris were buried under incoming cards
        // — the most expensive moment in the surface, spent invisibly.
        // 78ms between cards rather than 90: the arc now carries the ceremony,
        // so the gap can tighten and pay for most of the lead-in.
        cardWrapper.style.setProperty('--card-delay', `${leadIn + index * 78}ms`);
        cardWrapper.style.zIndex = String(Math.round(depth * 10));
        if (item.isStellar) {
          cardWrapper.dataset.stellar = 'true';
        }

        const inner = document.createElement('div');
        inner.className = 'card-pack-card-inner';

        const back = document.createElement('div');
        back.className = 'card-pack-card-face card-pack-card-back';
        const backCard = document.createElement('div');
        backCard.className = 'card card-back deck-builder-card';
        backCard.style.margin = '0';
        back.appendChild(backCard);
        applyEquippedCardback(backCard);
        // The fan rotates every card, and a rotated CSS background resamples
        // out of a 1x raster — measurably softer than the FRONT this flips
        // into, which escapes the same rotation through the oversampled canvas
        // raster. Give the back the same pixel budget (best-effort: a failure
        // just leaves the CSS background painting).
        // Face-down cards are physical too: tilt the back through the wrapper
        // host exactly like the revealed front (the back's own pointer-events
        // are disabled, so auto-registration alone never activates).
        if (CardTilt?.bindHost) {
          CardTilt.bindHost(cardWrapper, backCard);
        }

        const front = document.createElement('div');
        front.className = 'card-pack-card-face card-pack-card-front';

        inner.appendChild(back);
        inner.appendChild(front);
        cardWrapper.appendChild(inner);

        container.appendChild(cardWrapper);
        paintPackCardBack(backCard);
        attachPackHoverPreview(cardWrapper);
      });
      // SYNCHRONOUSLY, not on the next frame. `--fan-origin-dy` is read by
      // packCardFan's 0% frame, and a restored pack deals with zero delay — so
      // publishing it a frame later would let the first card start from the
      // fallback origin. This runs before the first paint of the new fan.
      measurePackStage();
    }

    view.forEach((item, index) => {
      const cardWrapper = container.querySelector(`.card-pack-card[data-index="${index}"]`);
      if (!cardWrapper) return;
      if (item.isStellar) {
        cardWrapper.dataset.stellar = 'true';
      } else {
        delete cardWrapper.dataset.stellar;
      }
      if (item.claimed) {
        revealPackCard(cardWrapper, item);
      } else {
        cardWrapper.setAttribute('aria-label', uiText('collection.packs.revealCard',
          'Reveal card {{number}}: {{name}}', { number: index + 1, name: item.cardName }));
      }
    });

    scheduleSpreadUpdate();
  }

  // Face-down packs wear whatever back the player equipped in the Cardbacks
  // tab. `.card-back` already reads `--cardback-image`, and the raster reads
  // the element's computed background, so setting the variable is enough.
  function applyEquippedCardback(backCard) {
    if (!backCard) return;
    backCard.style.setProperty(
      '--cardback-image',
      equippedCardback.kind === 'custom-pending'
        ? 'none'
        : `url(${JSON.stringify(equippedCardback.url)})`,
    );
  }

  function sanitizeCardbackUrl(value) {
    const shared = CardbackArt?.sanitizeUrl;
    if (typeof shared === 'function') return shared(value);
    if (typeof value !== 'string') return '';
    const source = value.trim();
    if (!source) return '';
    let pathname = '';
    try {
      const parsed = new URL(source, window.location?.href || 'http://localhost/');
      if (!['http:', 'https:', 'blob:', 'data:'].includes(parsed.protocol)) return '';
      if (parsed.protocol === 'data:' && !/^data:image\//i.test(source)) return '';
      pathname = parsed.pathname;
      for (let count = 0; count < 4; count += 1) {
        const decoded = decodeURIComponent(pathname);
        if (decoded === pathname) break;
        pathname = decoded;
      }
    } catch (_) {
      return '';
    }
    return pathname.toLowerCase() === '/assets/art_placeholder.webp' ? '' : source;
  }

  function normalizeCardbackDescriptor(value) {
    const slug = typeof value?.slug === 'string' && value.slug.trim()
      ? value.slug.trim()
      : 'default';
    if (slug === 'default') {
      return { kind: 'default', slug: 'default', url: '/assets/images/cardback.webp' };
    }
    const url = sanitizeCardbackUrl(value?.url);
    return url
      ? { kind: 'custom-ready', slug, url }
      : { kind: 'custom-pending', slug, url: '' };
  }

  function repaintEquippedCardbacks() {
    const section = getSection();
    section?.querySelectorAll('.card-pack-card-back .card-back').forEach((back) => {
      CardBackRaster?.clear?.(back);
      applyEquippedCardback(back);
      paintPackCardBack(back);
    });
  }

  function setEquippedCardback(descriptor) {
    const next = normalizeCardbackDescriptor(descriptor);
    if (next.slug !== equippedCardback.slug
      || next.kind !== equippedCardback.kind
      || next.url !== equippedCardback.url) {
      equippedCardback = next;
      repaintEquippedCardbacks();
    }
  }

  function stopCardbackObserver() {
    cardbackObserver?.dispose?.();
    cardbackObserver = null;
  }

  function ensureCardbackObserver() {
    if (equippedCardback.kind !== 'custom-pending' || cardbackObserver) return;
    const section = getSection();
    const observe = CardbackArt?.observe;
    if (!section || typeof observe !== 'function') return;
    const slug = equippedCardback.slug;
    cardbackObserver = observe(section, slug, (descriptor) => {
      if (equippedCardback.slug !== slug) return;
      setEquippedCardback(descriptor);
    });
  }

  function paintPackCardBack(backCard) {
    const paint = CardBackRaster?.paint;
    if (typeof paint !== 'function' || !backCard) return;
    try {
      Promise.resolve(paint(backCard)).catch(() => undefined);
    } catch (_) { /* presentation only: the CSS background still paints */ }
  }

  function repaintPackCardBacks() {
    const container = document.getElementById('card-packs-cards');
    if (!container) return;
    container
      .querySelectorAll('.card-pack-card:not(.card-pack-card-flip-complete) .card-pack-card-back .card-back')
      .forEach(paintPackCardBack);
  }

  // The flip hides the back for good; drop its backing store with it.
  function releasePackCardBack(cardWrapper) {
    const clear = CardBackRaster?.clear;
    if (typeof clear !== 'function') return;
    const backCard = cardWrapper?.querySelector('.card-pack-card-back .card-back');
    if (backCard) clear(backCard);
  }

  const MAX_WARMED_ART = 24;
  const WARM_ART_TIMEOUT_MS = 10000;
  const warmedArt = new Map();

  function retireWarmArt(url, record, { keepSource = false } = {}) {
    if (!record) return;
    if (record.timer !== null) clearTimeout(record.timer);
    record.timer = null;
    record.image.onload = null;
    record.image.onerror = null;
    if (!keepSource) {
      if (typeof record.image.removeAttribute === 'function') record.image.removeAttribute('src');
      else record.image.src = '';
    }
    if (warmedArt.get(url) === record && !keepSource) warmedArt.delete(url);
  }

  function warmArt(url) {
    if (!url || typeof Image === 'undefined') return;
    const retained = warmedArt.get(url);
    if (retained) {
      warmedArt.delete(url);
      warmedArt.set(url, retained);
      return;
    }
    const image = new Image();
    const record = { image, timer: null };
    image.decoding = 'async';
    image.onload = () => retireWarmArt(url, record, { keepSource: true });
    image.onerror = () => retireWarmArt(url, record);
    record.timer = setTimeout(() => retireWarmArt(url, record), WARM_ART_TIMEOUT_MS);
    image.src = url;
    warmedArt.set(url, record);
    while (warmedArt.size > MAX_WARMED_ART) {
      const [oldestUrl, oldestRecord] = warmedArt.entries().next().value;
      retireWarmArt(oldestUrl, oldestRecord);
    }
  }

  /**
   * Warm the card BACK before the fan is dealt.
   *
   * `.card-back` falls back to `background-color: rgba(0,0,0,0.5)` until its
   * image decodes, so the first dealt card could flash as a black slab in the
   * middle of the tear. Best-effort and non-blocking (docs/asset-loading.md
   * law 3): the fan is never gated on this, it just usually wins the race.
   */
  function warmCardback() {
    if (typeof Image === 'undefined') return;
    if (equippedCardback.url) warmArt(equippedCardback.url);
  }

  // The face-down cards' art is warmed ONCE, by `prewarmKnownPackArt` the
  // moment the opening's metadata names it (setActivePack): high priority,
  // CORS-mode and decoded into the shared raster authority the flip reads
  // from. A second warm here re-requested every card through a plain, non-CORS
  // `Image` — a separate cache entry and a second decode racing the first.

  function scheduleSpreadUpdate() {
    if (spreadRaf) cancelAnimationFrame(spreadRaf);
    spreadRaf = requestAnimationFrame(() => {
      spreadRaf = null;
      measurePackStage();
      updateCardSpread();
      // A resize re-lays the fan: the backs' backing stores were cut for the
      // old box. Repainting is a no-op when the geometry did not move.
      repaintPackCardBacks();
    });
  }

  /**
   * Re-derive the fan's geometry from the BOX, not from the event.
   *
   * `window.resize` fires once, at the START of a phone's rotation, while the
   * browser is still animating and the stage has not reached its new size. The
   * single frame `scheduleSpreadUpdate` then measures is therefore the OLD
   * layout — or, when the surface is not on screen at all, no layout — and
   * nothing fires again once the box settles. That is how a rotation could
   * leave the reveal fan stacked and keep it stacked through every later
   * rotation.
   *
   * A ResizeObserver answers the only question that actually matters here:
   * has the box these lengths are derived from changed? It fires on the late
   * settle at the end of the rotation, when a hidden tab is shown again, and
   * when iOS collapses the URL bar — none of which produce a second `resize`.
   * The window listener stays as the floor for anything without the API.
   *
   * No feedback loop: the callback writes custom properties on the fan's cards
   * and on the glow, neither of which alters an observed element's own box,
   * and the work is rAF-coalesced regardless.
   */
  function observeFanGeometry() {
    if (typeof ResizeObserver !== 'function') return;
    if (!fanGeometryObserver) fanGeometryObserver = new ResizeObserver(scheduleSpreadUpdate);
    fanGeometryObserver.disconnect();
    ['card-packs-stage', 'card-packs-pack', 'card-packs-cards']
      .map((id) => document.getElementById(id))
      .filter(Boolean)
      .forEach((el) => fanGeometryObserver.observe(el));
  }

  /**
   * Publish the pack's LAYOUT geometry to CSS. Presentation only.
   *
   * Two effects need to know where the pack physically is, and neither may read
   * a transformed box:
   *
   *  - the ground contact, which is the pack's sibling so that the float cannot
   *    carry it. Measuring `getBoundingClientRect` here would feed the float
   *    straight back in and restore the wobble the sibling move removes.
   *  - the deal's origin, so the cards come out of the torn pack instead of
   *    from a fixed offset 94px below it.
   *
   * `offsetTop/offsetLeft/offsetWidth/offsetHeight` are layout values and
   * ignore every transform, which is exactly the property both of these want.
   */
  function measurePackStage() {
    const pack = document.getElementById('card-packs-pack');
    const stage = document.getElementById('card-packs-stage');
    if (!pack || !stage || !pack.offsetHeight) return;

    const glow = stage.querySelector('.card-packs-pack-glow');
    if (glow) {
      glow.style.setProperty('--pack-shadow-top', `${pack.offsetTop + pack.offsetHeight}px`);
      glow.style.setProperty('--pack-shadow-width', `${Math.round(pack.offsetWidth * 0.74)}px`);
    }

    const container = document.getElementById('card-packs-cards');
    const card = container?.querySelector('.card-pack-card-inner');
    if (!container || !card?.offsetHeight) return;
    // A fan card's untransformed centre sits half a card above the container's
    // bottom edge; the pack's centre is where the rip happens. The difference
    // is how far the deal has to travel, and it is negative (upward) on every
    // stage where the pack sits above the fan.
    const restCentre = container.offsetTop + container.offsetHeight - card.offsetHeight / 2;
    const packCentre = pack.offsetTop + pack.offsetHeight / 2;
    container.style.setProperty('--fan-origin-dy', `${Math.round(packCentre - restCentre)}px`);
  }

  function updateCardSpread() {
    const container = document.getElementById('card-packs-cards');
    const stage = document.getElementById('card-packs-stage');
    if (!container || !stage || !CardPacksData?.calculatePackSpread) return;
    if (!activePack || !activePack.opened) return;

    const inner = container.querySelector('.card-pack-card-inner') || container.querySelector('.card');
    if (!inner) return;

    const stageRect = stage.getBoundingClientRect();
    // Layout width, never getBoundingClientRect: the measured card may be
    // mid-flip (rotateY projection narrows its client rect) or hover-scaled,
    // and a projected width would respace the whole fan on every reveal.
    const cardWidth = inner.offsetWidth || inner.getBoundingClientRect().width;

    // A BOX WITH NO WIDTH IS NOT A MEASUREMENT. This value is published as an
    // inline pixel length, so it outranks the stylesheet from the moment it is
    // written and nothing but another measurement can take it back. Any resize
    // that arrives while the surface is not laid out — the Collection on
    // another tab, the modal closed, a phone still mid-rotation — reads zero
    // here, and `calculatePackSpread` faithfully returns 0 for a zero-width
    // card. Writing that stacked all five cards on one spot, permanently:
    // reported as "rotating collapses the cards closer together, becomes
    // unusable even when rotated back to landscape". The card SIZE recovered on
    // its own because it is pure CSS, which is exactly why the reported
    // screenshot shows correctly-sized cards far too close together.
    // Skipping leaves the stylesheet's own `calc(var(--card-width) * 0.5)` in
    // charge, and `observeFanGeometry` guarantees another call the moment the
    // box becomes real.
    if (!(stageRect.width > 0) || !(cardWidth > 0)) return;

    const count = Array.isArray(activePack?.items) ? activePack.items.length : 0;
    const spread = CardPacksData.calculatePackSpread({
      stageWidth: stageRect.width,
      cardWidth,
      count,
      buffer: cardWidth * 0.2,
      hoverScale: 1,
    });

    if (!Number.isFinite(spread)) return;
    container.style.setProperty('--card-spread', `${Math.max(0, Math.floor(spread))}px`);
  }

  function armPackCardFlipFlattening(cardWrapper, inner) {
    if (!cardWrapper || !inner || cardWrapper.dataset.flipFlattenArmed === 'true') return;
    cardWrapper.dataset.flipFlattenArmed = 'true';
    let finished = false;
    const finish = (event) => {
      if (finished) return;
      if (event && (event.target !== inner || event.propertyName !== 'transform')) return;
      finished = true;
      inner.removeEventListener('transitionend', finish);
      inner.removeEventListener('transitioncancel', finish);
      delete cardWrapper.dataset.flipFlattenArmed;
      if (cardWrapper.isConnected && cardWrapper.classList.contains('card-pack-card-revealed')) {
        cardWrapper.classList.add('card-pack-card-flip-complete');
        releasePackCardBack(cardWrapper);
      }
    };
    inner.addEventListener('transitionend', finish);
    inner.addEventListener('transitioncancel', finish);
    window.setTimeout(() => finish(), 750);
  }

  /**
   * Everything a face shows, and the signature a rendered face is compared by.
   * `item` is a `buildPackCardView` entry, so a face prepared at the press and
   * one requested by the claim's render describe the card identically.
   */
  function describePackFace(item) {
    const resolvedCard = item.card
      || allCards.find((entry) => entry.id === item.cardId)
      || null;
    // The claim is already authoritative. Missing collection metadata must
    // not strand an owned card face-down or invent its mechanics. The unknown
    // face keeps the real card id so the shared loader can continue resolving
    // its art without replacing it with placeholder artwork.
    const card = resolvedCard || {
      id: item.cardId,
      name: uiText('collection.achievements.unknownCard', 'Unknown Card'),
      type: 'Unknown',
      cost: 0,
      description: '',
      targetingType: 'None',
    };
    const metadataUnavailable = !resolvedCard;
    const variant = item.isStellar ? 'stellar' : 'normal';
    const stellarAsset = variant === 'stellar' ? getStellarAsset(item.cardId) : null;
    const cardData = buildCardElementData(card, variant, stellarAsset);
    const label = uiText('collection.packs.revealedCard', 'Revealed card: {{name}}',
      { name: metadataUnavailable ? card.name : (item.cardName || card.name) });
    return {
      cardData,
      variant,
      metadataUnavailable,
      label,
      signature: JSON.stringify([cardData, metadataUnavailable, label]),
    };
  }

  function packViewItem(index) {
    if (!activePack || !Array.isArray(activePack.items) || !CardPacksData?.buildPackCardView) return null;
    return CardPacksData.buildPackCardView({ packItems: activePack.items, allCards })[index] || null;
  }

  // The faces a card front paints with (cards.js `waitForCardFonts`). They are
  // loaded long before a pack is opened, so this is normally null: nothing to
  // wait for.
  const PACK_FACE_FONTS = [
    { font: '700 16px "Spectral"', text: undefined },
    { font: '500 16px "Spectral"', text: undefined },
    { font: '700 16px "Moonfall Mana Numerals"', text: '0123456789' },
  ];

  function pendingPackFaceFonts() {
    const fonts = typeof document !== 'undefined' ? document.fonts : null;
    if (typeof fonts?.load !== 'function') return null;
    try {
      if (typeof fonts.check === 'function'
        && PACK_FACE_FONTS.every(({ font, text }) => fonts.check(font, text))) return null;
      return Promise.all(PACK_FACE_FONTS.map(({ font, text }) => fonts.load(font, text)))
        .then(() => undefined, () => undefined);
    } catch (_) {
      return null;
    }
  }

  /**
   * Build a card's face behind its back and start settling it: the real art
   * decoded into its final-geometry raster, and the card faces loaded. The
   * front stays `visibility: hidden` and `aria-hidden` until a reveal adopts
   * the token and turns the card.
   */
  function preparePackFace(cardWrapper, item, face) {
    const front = cardWrapper.querySelector('.card-pack-card-front');
    const wasRevealed = cardWrapper.classList.contains('card-pack-card-revealed');
    const pending = pendingRevealPreparations.get(cardWrapper);
    const cardEl = Cards.createCardElement(
      face.cardData,
      `card-pack-${activePack?.id || 'pack'}-${item.cardId}-${face.variant}`
    );
    cardEl.style.margin = '0';
    cardEl.classList.add('deck-builder-card', 'card-tilt-disabled');
    if (face.metadataUnavailable) {
      cardEl.dataset.cardMetadataUnavailable = 'true';
      const cost = cardEl.querySelector('.card-cost');
      if (cost) {
        cost.hidden = true;
        cost.setAttribute('aria-hidden', 'true');
      }
    }
    if (face.variant === 'stellar') {
      cardEl.dataset.stellarPreview = 'collection';
    }
    const token = {
      packGeneration: openGeneration,
      packSnapshot: activePack,
      cardId: item.cardId,
      isStellar: Boolean(item.isStellar),
      signature: face.signature,
      cardEl,
      revealing: false,
      ready: null,
    };

    // A same-pack state refresh replaces `activePack` without replacing the
    // physical fan. Its claimed card must begin a fresh preparation now;
    // waiting on the obsolete snapshot lets that stale task remove itself and
    // leaves the already-owned card face-down forever. Remove only the old
    // task's private node — its finalizer is token-fenced from the new state.
    front.querySelectorAll('.card').forEach(oldCard => {
      CardTilt?.unregister?.(oldCard);
      oldCard.querySelectorAll('.card-art-paint').forEach(paint => Cards?.disposeCardArtImage?.(paint));
    });
    pending?.cardEl?.remove();
    if (activePackPreview()?.__packSource === cardWrapper) dismissPackPreview();
    pendingRevealPreparations.set(cardWrapper, token);
    if (!wasRevealed) {
      cardWrapper.dataset.rasterRevealPending = 'true';
      front.classList.add('card-pack-card-front-preparing');
      front.setAttribute('aria-hidden', 'true');
    }
    // Keep an unchanged face and its decoded paint; replace only a changed
    // face. Withdrawn metadata must not remain readable behind a loading gate.
    front.replaceChildren(cardEl);

    let work;
    try {
      const settle = Cards?.settleCardRasterForPresentation;
      work = typeof settle === 'function' && settle(cardEl, face.cardData, {
        oversample: PACK_CARD_RASTER_OVERSAMPLE,
        stableGeometry: true,
      });
    } catch (_) {
      work = false;
    }
    const settled = Promise.resolve(work);
    const fonts = pendingPackFaceFonts();
    token.ready = fonts ? Promise.all([settled, fonts]).then(([value]) => value) : settled;
    return token;
  }

  function tokenDescribesItem(token, item, face) {
    return Boolean(token)
      && token.packGeneration === openGeneration
      && token.packSnapshot === activePack
      && token.cardId === item.cardId
      && token.isStellar === Boolean(item.isStellar)
      && token.signature === face.signature;
  }

  /** A preparation no reveal adopted (the claim was refused or superseded). */
  function discardPreparedPackFace(cardWrapper) {
    const token = cardWrapper ? pendingRevealPreparations.get(cardWrapper) : null;
    if (!token || token.revealing) return;
    pendingRevealPreparations.delete(cardWrapper);
    token.cardEl.querySelectorAll('.card-art-paint').forEach(paint => Cards?.disposeCardArtImage?.(paint));
    token.cardEl.remove();
    if (cardWrapper.classList.contains('card-pack-card-revealed')) return;
    delete cardWrapper.dataset.rasterRevealPending;
    const front = cardWrapper.querySelector('.card-pack-card-front');
    front?.classList.remove('card-pack-card-front-preparing');
    front?.removeAttribute('aria-hidden');
  }

  /**
   * A card whose face is being made ready: past the shared art grace (CSS) a
   * charge sweep crosses its back, so a slow face reads as working, never as
   * broken. `pressed` marks the card the player pressed (a restored card just
   * waits); it draws nothing — the gold rim and brighter back it once held
   * until the flip were reverted (the owner, 28 September 2026).
   */
  function setPackCardCharging(cardWrapper, charging, { pressed = false } = {}) {
    if (!cardWrapper) return;
    cardWrapper.classList.toggle('card-pack-card-charging', charging);
    if (!charging || pressed) cardWrapper.classList.toggle('card-pack-card-pressed', charging);
    if (charging) cardWrapper.setAttribute('aria-busy', 'true');
    else cardWrapper.removeAttribute('aria-busy');
  }

  function afterNextPaint(callback) {
    const later = () => setTimeout(callback, 0);
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(later);
    else later();
  }

  /**
   * Start the face of the card the player just pressed while its claim is in
   * flight, so the flip waits on the round-trip alone. After the press's own
   * frame has painted, so the acknowledgement is never delayed by it. The pack
   * contents are already the client's authoritative state (charge time), and
   * the face stays hidden until the accepted claim adopts it.
   */
  function preparePressedPackFace(cardWrapper, index, pendingClaimKey) {
    const generation = openGeneration;
    const snapshot = activePack;
    afterNextPaint(() => {
      if (generation !== openGeneration || activePack !== snapshot) return;
      if (!pendingClaims.has(pendingClaimKey) || !cardWrapper.isConnected) return;
      if (!Cards?.createCardElement || cardWrapper.classList.contains('card-pack-card-revealed')) return;
      if (pendingRevealPreparations.has(cardWrapper)) return;
      const item = packViewItem(index);
      if (!item || item.claimed) return;
      preparePackFace(cardWrapper, item, describePackFace(item));
    });
  }

  function holdPackFlip(ready) {
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(null), PACK_FLIP_HOLD_CAP_MS);
      Promise.resolve(ready).then((value) => {
        clearTimeout(timer);
        resolve(value);
      }, () => {
        clearTimeout(timer);
        resolve(false);
      });
    });
  }

  async function revealPackCard(cardWrapper, item) {
    if (!cardWrapper || !item) return;
    const wasRevealed = cardWrapper.classList.contains('card-pack-card-revealed');

    const front = cardWrapper.querySelector('.card-pack-card-front');
    const inner = cardWrapper.querySelector('.card-pack-card-inner');
    if (!front || !inner || !Cards?.createCardElement) return;

    const face = describePackFace(item);
    const { variant, label, signature } = face;
    if (wasRevealed && renderedPackFaces.get(cardWrapper) === signature) return;
    const packGeneration = openGeneration;
    const packSnapshot = activePack;
    const itemIndex = Number(cardWrapper.dataset.index);
    const pending = pendingRevealPreparations.get(cardWrapper);
    const adoptable = tokenDescribesItem(pending, item, face)
      && pending.cardEl.parentElement === front;
    // Another reveal already owns this exact preparation and will finish it.
    if (adoptable && pending.revealing) return;
    const token = adoptable ? pending : preparePackFace(cardWrapper, item, face);
    token.revealing = true;
    const { cardEl } = token;

    let settled = null;
    if (wasRevealed) {
      void Promise.resolve(token.ready).catch(() => false);
    } else {
      // Never turn onto a face that is still loading. The art has been warming
      // since the charge, so this is normally already settled; a slow one keeps
      // the card face-down and visibly charging, up to the hold's cap.
      setPackCardCharging(cardWrapper, true);
      settled = await holdPackFlip(token.ready);
    }

    const currentItem = Number.isFinite(itemIndex)
      ? activePack?.items?.[itemIndex]
      : null;
    const isCurrent = pendingRevealPreparations.get(cardWrapper) === token
      && packGeneration === openGeneration
      && activePack === packSnapshot
      && currentItem?.claimed === true
      && currentItem?.cardId === item.cardId
      && Boolean(currentItem?.isStellar) === Boolean(item.isStellar)
      && cardWrapper.isConnected
      && front.isConnected
      && cardEl.parentElement === front;

    if (!isCurrent) {
      if (pendingRevealPreparations.get(cardWrapper) === token) {
        pendingRevealPreparations.delete(cardWrapper);
        delete cardWrapper.dataset.rasterRevealPending;
        front.classList.remove('card-pack-card-front-preparing');
        front.removeAttribute('aria-hidden');
        setPackCardCharging(cardWrapper, false);
      }
      cardEl.remove();
      return;
    }

    if (!wasRevealed && settled !== true) {
      // The card is ALREADY the player's (the claim committed server-side).
      // Holding past the cap would leave a dead face-down card, so it turns
      // text-first and its art well keeps its own loading/unavailable
      // treatment (docs/asset-loading.md law 2).
      console.warn('[CardPacks] Revealing claimed card text-first; raster did not settle:', item.cardId);
    }

    pendingRevealPreparations.delete(cardWrapper);
    delete cardWrapper.dataset.rasterRevealPending;
    front.classList.remove('card-pack-card-front-preparing');
    front.removeAttribute('aria-hidden');
    cardEl.classList.remove('card-tilt-disabled');
    if (!wasRevealed) armPackCardFlipFlattening(cardWrapper, inner);
    if (CardTilt?.bindHost) {
      CardTilt.bindHost(cardWrapper, cardEl);
    }
    // The charge ends on the frame the card starts to turn.
    setPackCardCharging(cardWrapper, false);
    cardWrapper.classList.add('card-pack-card-revealed');
    renderedPackFaces.set(cardWrapper, signature);
    const revealCueKey = `${activePack?.id}:${cardWrapper.dataset.index}`;
    const revealCue = pendingRevealCues.get(revealCueKey);
    pendingRevealCues.delete(revealCueKey);
    if (!wasRevealed && revealCue
        && revealCue.packGeneration === openGeneration
        && revealCue.packId === activePack?.id
        && revealCue.cardId === item.cardId) {
      /* The cue fires a long way from the tap — a Convex round-trip and an
         awaited raster settle sit between them, ample time for Firefox to
         suspend the pool's idle decoders. The wait window turns that silent
         drop into a deferral: the reveal sound follows within the flip's own
         beat instead of vanishing ("no card reveal sound. at least not
         reliably"). */
      playSound('card_reveal', { waitForLoadMs: 1200 });
      if (revealCue.isStellar) playSound('stellar', { waitForLoadMs: 1200 });
    }
    // Claiming is finished, but this remains an active inspection button.
    // handleClaimCard rejects a claimed slot independently of accessibility.
    cardWrapper.removeAttribute('aria-disabled');
    cardWrapper.setAttribute('aria-label', label);
    enableRevealedPackTouchPreview(cardWrapper);

    if (variant === 'stellar' && Cards?.setStellarHover && cardWrapper.dataset.stellarHoverAttached !== 'true') {
      cardWrapper.dataset.stellarHoverAttached = 'true';
      cardWrapper.addEventListener('mouseenter', (event) => {
        if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
        Cards.setStellarHover(cardWrapper.querySelector('.card-pack-card-front > .card'), true);
      });
      // A Stellar card animates at REST, not only under the pointer.
      // `setStellarHover(el, false)` hands the card back to its resting loop
      // itself — that is the seam's job now, so no surface re-arms it by hand.
      cardWrapper.addEventListener('mouseleave', () => {
        Cards.setStellarHover(cardWrapper.querySelector('.card-pack-card-front > .card'), false);
      });
    }
  }

  /** @typedef {HTMLElement & { __packSource?: HTMLElement }} PackPreviewElement */

  /**
   * The pack's own popup, if it is what `#card-hover-preview` currently holds.
   * The shared element is rebuilt by every catalog; only a pack-built one
   * carries `__packSource`, the fan card it was opened from.
   * @returns {PackPreviewElement | null}
   */
  function activePackPreview() {
    const preview = document.getElementById('card-hover-preview');
    return preview?.classList.contains('card-packs-hover-preview')
      ? /** @type {PackPreviewElement} */ (preview)
      : null;
  }

  function dismissPackPreview() {
    const preview = activePackPreview();
    if (!preview) return;
    const previewCard = preview.querySelector('.card');
    if (previewCard?.classList?.contains('stellar-card') && Cards?.setStellarHover) {
      Cards.setStellarHover(previewCard, false);
    }
    preview.remove();
  }

  // THE ENLARGED CARD OWNS ITS WHOLE SURFACE. The popup takes the pointer
  // (`.card-packs-hover-preview` is `pointer-events: auto`): it is up to 3.5x
  // its source and hangs over both neighbours, and while it was transparent
  // the hover went to whichever neighbour sat under the card the player was
  // reading. Its lifetime still keys on real pointer GEOMETRY rather than
  // `mouseleave` — the fan's tilt/straighten reflow fires spurious leaves
  // under a stationary pointer — so it dies only when the pointer is neither
  // on the popup nor in the source card's padded box, or the source leaves
  // the DOM.
  const SOURCE_EXIT_PAD_PX = 14;
  let packPreviewWatchdogInstalled = false;

  function pointerOnPackPreview(preview, event) {
    // The engine's own hit-test is the oracle. A tilted card recedes a few
    // pixels at the edge under the pointer; where the engine stops counting
    // the pointer as on the card, so does the popup.
    return event.target instanceof Node && preview.contains(event.target);
  }

  function pointerInsidePackSource(source, event) {
    const rect = source.getBoundingClientRect();
    const pad = uiPx(SOURCE_EXIT_PAD_PX);
    return event.clientX >= rect.left - pad
      && event.clientX <= rect.right + pad
      && event.clientY >= rect.top - pad
      && event.clientY <= rect.bottom + pad;
  }

  function installPackPreviewWatchdog() {
    if (packPreviewWatchdogInstalled || typeof document === 'undefined') return;
    packPreviewWatchdogInstalled = true;
    document.addEventListener('pointermove', (event) => {
      const preview = activePackPreview();
      if (!preview) return;
      const source = preview.__packSource;
      if (!source || !source.isConnected) {
        dismissPackPreview();
        return;
      }
      if (pointerOnPackPreview(preview, event) || pointerInsidePackSource(source, event)) return;
      dismissPackPreview();
    }, { passive: true });
  }

  function enableRevealedPackTouchPreview(cardWrapper) {
    if (!cardWrapper?.classList.contains('card-pack-card-revealed')) return;
    if (cardWrapper.dataset.touchPreviewAttached === 'true') return;
    const attach = TouchPreview?.attach;
    if (typeof attach !== 'function') return;
    const options = revealedTouchPreviewOptions.get(cardWrapper);
    if (options) attach(cardWrapper, options);
    else attach(cardWrapper);
    cardWrapper.dataset.touchPreviewAttached = 'true';
  }

  function attachPackHoverPreview(cardWrapper) {
    if (!cardWrapper || cardWrapper.dataset.hoverAttached === 'true') return;
    cardWrapper.dataset.hoverAttached = 'true';
    let hoverActive = false;
    let hoverGeneration = 0;

    if (!PACK_HOVER_PREVIEW_ENABLED) {
      cardWrapper.addEventListener('mouseenter', (event) => {
        if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
        const preview = document.getElementById('card-hover-preview');
        if (preview?.classList?.contains('card-packs-hover-preview')) {
          preview.remove();
        }
      });
      revealedTouchPreviewOptions.set(cardWrapper, null);
      return;
    }

    // Sweeping the fan must not spawn a popup per crossed card: the enlarged
    // inspection opens only after the pointer settles on one card.
    const HOVER_INTENT_MS = 180;
    let hoverIntentTimer = null;
    cardWrapper.addEventListener('mouseenter', (event) => {
      if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
      if (!cardWrapper.classList.contains('card-pack-card-revealed')) return;
      // Already inspecting this card. The popup covers its source, so an entry
      // here is the pointer finding an exposed sliver of it (a popup clamped
      // into the viewport); tearing the popup down to rebuild it would flicker
      // and cost a raster.
      if (activePackPreview()?.__packSource === cardWrapper) {
        hoverActive = true;
        return;
      }
      if (!Cards?.createCardElement) return;
      if (!activePack || !Array.isArray(activePack.items)) return;

      const index = Number(cardWrapper.dataset.index);
      if (!Number.isFinite(index)) return;
      const item = activePack.items[index];
      if (!item || !item.claimed) return;
      const card = allCards.find((entry) => entry.id === item.cardId);
      if (!card) return;

      const intent = {
        generation: ++hoverGeneration,
        packGeneration: openGeneration,
        packSnapshot: activePack,
        index,
        item,
        card,
      };
      hoverActive = true;
      clearTimeout(hoverIntentTimer);
      if (event?.isTrusted === false) {
        // Synthetic entries come from the resolved touch tap, which already
        // established intent — open immediately.
        void openRevealedCardPreview(intent);
        return;
      }
      hoverIntentTimer = setTimeout(() => {
        void openRevealedCardPreview(intent);
      }, HOVER_INTENT_MS);
    });

    function ownsPreviewIntent(intent) {
      return Boolean(
        intent
        && hoverActive
        && intent.generation === hoverGeneration
        && intent.packGeneration === openGeneration
        && activePack === intent.packSnapshot
        && activePack?.items?.[intent.index] === intent.item
        && allCards.find(card => card.id === intent.item.cardId) === intent.card
        && intent.item?.claimed
        && cardWrapper.isConnected
        && cardWrapper.classList.contains('card-pack-card-revealed')
      );
    }

    async function openRevealedCardPreview(intent) {
      // Validate before touching `activePack` or beginning raster work. A
      // trusted mouse entry deliberately waits 180ms; close/account switches
      // during that interval invalidate the intent at its point of origin.
      if (!ownsPreviewIntent(intent)) return;
      const {
        generation,
        packGeneration,
        packSnapshot,
        index,
        item,
        card,
      } = intent;

      if (Cards?.clearHoverPreview) {
        Cards.clearHoverPreview();
      }

      const sourceCard = cardWrapper.querySelector('.card-pack-card-front > .card');
      const prepareRaster = Cards.prepareCardRasterForInteraction;
      if (typeof prepareRaster === 'function') {
        try {
          await withPackArtGrace(prepareRaster(sourceCard, 'hover'));
        } catch (_) { /* Text inspection remains available while art recovers. */ }
      }
      if (!ownsPreviewIntent(intent)) return;

      const sourceRect = cardWrapper.getBoundingClientRect();

      const preview = /** @type {PackPreviewElement} */ (document.createElement('div'));
      preview.id = 'card-hover-preview';
      preview.className = 'card-hover-preview card-packs-hover-preview';
      preview.style.zIndex = Cards.HOVER_PREVIEW_Z_INDEX || '2147483647';
      preview.style.left = `${Math.round(sourceRect.left)}px`;
      preview.style.top = `${Math.round(sourceRect.top)}px`;
      preview.style.visibility = 'hidden';

      const variant = item.isStellar ? 'stellar' : 'normal';
      const stellarAsset = variant === 'stellar' ? getStellarAsset(item.cardId) : null;
      const cardData = buildCardElementData(card, variant, stellarAsset);
      const cardEl = Cards.createCardElement(
        cardData,
        `card-pack-hover-${item.cardId}-${variant}`
      );
      cardEl.style.transformOrigin = 'center center';
      cardEl.style.setProperty('--card-hover-transform', 'scale(1) rotate(0deg) translateY(0%)');
      if (variant === 'stellar') {
        cardEl.dataset.stellarPreview = 'collection';
      }
      preview.appendChild(cardEl);

      // The popup takes the pointer and owns its whole surface; the geometry
      // watchdog above decides when it lets go.
      preview.__packSource = cardWrapper;
      installPackPreviewWatchdog();

      document.body.appendChild(preview);

      if (Cards?.applyPreviewScale) {
        // The popup adopts the resting card's description: same size step,
        // same lines (Cards: ENGRAVED DESCRIPTIONS).
        Cards.applyPreviewScale(cardEl, variant, sourceRect, preview, {
          ...Cards.HOVER_PREVIEW_OPTIONS,
          sourceCard,
        });
      }

      try {
        await withPackArtGrace(Cards.settleCardRasterForPresentation?.(
          cardEl,
          cardData,
          {
            oversample: PACK_CARD_RASTER_OVERSAMPLE,
            stableGeometry: true,
            sourceCard,
            coverVisualExpansion: true,
          }
        ));
      } catch (_) { /* Keep the card inspectable; its art well owns recovery. */ }
      if (
        !ownsPreviewIntent({
          generation,
          packGeneration,
          packSnapshot,
          index,
          item,
          card,
        })
      ) {
        preview.remove();
        return;
      }

      if (CardTilt?.registerPreview) {
        /* The HOST hears the pointer, the card is the geometry — the catalog
           tile pattern. The host is the card's flat box, so a pointer sitting
           where the tilted card's edge has receded is still on the host and
           the tilt never oscillates at the edge. */
        CardTilt.registerPreview(cardEl, { host: preview });
      }
      if (variant === 'stellar' && Cards?.setStellarHover) {
        Cards.setStellarHover(cardEl, true);
      }
      preview.style.removeProperty('visibility');
    }
    /* Dismiss EXPLICITLY on touch. This surface deliberately ignores
       `mouseleave` for dismissal — the fan's tilt/straighten reflow fires
       spurious leaves under a stationary pointer, so the popup's lifetime keys
       on real pointer GEOMETRY instead. A finger never produces that geometry,
       so a tapped-open pack card had nothing that could ever close it:
       "not possible to re-collapse back card pack enlarged cards". Handing
       TouchPreview its own open/close pair keeps the mouse watchdog exactly as
       it is while giving touch a dismissal that does not depend on it. */
    revealedTouchPreviewOptions.set(cardWrapper, {
      onPreview: () => cardWrapper.dispatchEvent(new MouseEvent('mouseenter', { bubbles: false })),
      onDismiss: () => {
        hoverActive = false;
        clearTimeout(hoverIntentTimer);
        dismissPackPreview();
      },
    });

    cardWrapper.addEventListener('mouseleave', () => {
      // Spurious leaves fire while the tilt/straighten transition reflows the
      // card under a stationary pointer — only the pending open is cancelled
      // here; the geometry watchdog dismisses the popup on a REAL exit.
      hoverActive = false;
      clearTimeout(hoverIntentTimer);
    });
  }

  function render() {
    updateSummary();
    renderPackStage();
    renderPackCards();
  }

  // A debug port declares a Stellar rate; every ordinary port declares none.
  // Applying it here — before the config read — means the published odds and
  // the rolled odds are the same number, and stepping back to a normal port
  // clears the override instead of leaving the account permanently rigged.
  const DEBUG_ODDS_ARMED_KEY = 'moonfall:cardPacks:debugStellarOddsArmed';

  function readArmedMarker() {
    try { return window.localStorage?.getItem(DEBUG_ODDS_ARMED_KEY) === '1'; } catch (_) { return false; }
  }

  function writeArmedMarker(armed) {
    try {
      if (armed) window.localStorage?.setItem(DEBUG_ODDS_ARMED_KEY, '1');
      else window.localStorage?.removeItem(DEBUG_ODDS_ARMED_KEY);
    } catch (_) { /* private-mode storage is not worth failing the screen over */ }
  }

  async function syncDebugStellarOdds() {
    const setter = MoonfallCloud?.cardPacks?.setDebugStellarOdds;
    if (typeof setter !== 'function') return;
    const requested = window.MoonfallEnv?.cardPackStellarDebugOdds;
    const wanted = Number.isFinite(requested) ? requested : null;
    if (wanted === null && !readArmedMarker()) return;
    try {
      await setter(wanted === null ? undefined : wanted);
      writeArmedMarker(wanted !== null);
    } catch (err) {
      // Never block the packs screen: production refuses the mutation outright,
      // and a signed-out visitor simply has no account to arm.
      console.warn('[CardPacks] Stellar debug odds sync skipped:', err);
    }
  }

  async function loadConfig(generation) {
    if (!MoonfallCloud?.cardPacks?.getConfig) return;
    await syncDebugStellarOdds();
    if (generation !== openGeneration) return;
    try {
      const config = await MoonfallCloud.cardPacks.getConfig();
      if (generation !== openGeneration) return;
      applyConfig(config);
    } catch (err) {
      console.warn('[CardPacks] Failed to load config:', err);
      // A refresh keeps the last disclosure on screen only until its read
      // answers; failed terms cannot leave the old purchase gate open.
      if (generation === openGeneration) stellarOddsKnown = false;
    }
  }

  function applyConfig(config) {
      // Missing terms withdraw the disclosure (docs/card-packs.md): a refresh
      // keeps the last one on screen only until its read answers.
      if (!config) stellarOddsKnown = false;
      if (config) {
        openingCardVersion = config.openingCardVersion === 1 ? 1 : 0;
        if (openingCardVersion === 1) contextCards = [];
        // A malformed supplied value must disable purchasing, not reuse a
        // previous view's successful disclosure. Absence supports older servers.
        stellarOddsKnown = Number.isFinite(config.stellarOdds)
          && (config.stellarPackOdds === undefined || (typeof config.stellarPackOdds === 'number'
            && Number.isFinite(config.stellarPackOdds) && config.stellarPackOdds >= 0 && config.stellarPackOdds <= 1));
        packConfig.stellarPackOdds = config.stellarPackOdds;
        if (Number.isFinite(config.packPrice)) packConfig.packPrice = config.packPrice;
        if (Number.isFinite(config.packSize)) packConfig.packSize = config.packSize;
        if (Number.isFinite(config.stellarOdds)) {
          packConfig.stellarOdds = config.stellarOdds;
        }
        stellarDebugActive = config.stellarDebugActive === true;
        if (Number.isFinite(config.duplicateValuePerPack)) {
          duplicateValuePerPack = Math.max(1, Math.floor(config.duplicateValuePerPack));
        }
        if (Number.isFinite(config.normalDuplicateValue)) {
          normalDuplicateValue = Math.max(1, Math.floor(config.normalDuplicateValue));
        }
        if (Number.isFinite(config.stellarDuplicateValue)) {
          stellarDuplicateValue = Math.max(1, Math.floor(config.stellarDuplicateValue));
        }
      }
  }

  async function loadLegacyCardContext(generation, userId) {
    const cards = getLegacyCards ? await getLegacyCards() : contextCards;
    if (generation !== openGeneration || userId !== currentUserId) return false;
    if (!Array.isArray(cards)) throw new Error('pack_metadata_invalid');
    allCards = cards;
    return true;
  }

  async function loadPackState(generation, stateEpoch, userId, providedState = undefined,
    { keepFinished = false } = {}) {
    if (providedState === undefined && !MoonfallCloud?.cardPacks?.getPackState) return false;
    try {
      const state = providedState === undefined ? await MoonfallCloud.cardPacks.getPackState() : providedState;
      if (
        generation !== openGeneration
        || stateEpoch !== packStateEpoch
        || userId !== currentUserId
      ) return false;
      const nextItems = state?.activePack?.items || [];
      // A refresh that finds no pending opening keeps a finished one the player
      // is still looking at, described again (a language push asks for it).
      const kept = keepFinished && !state?.activePack ? finishedPackOnStage() : null;
      const [metadata] = await Promise.all([
        openingCardVersion === 1 ? readPackOpeningCards({ api: MoonfallCloud.cardPacks,
          pack: state?.activePack || kept, accountId: userId, language: currentLanguage(),
          signal: screenReadController?.signal }) : null,
        loadPackStellarAssets(nextItems),
        holdForPackWrapper(state?.activePack),
      ]);
      if (
        generation !== openGeneration
        || stateEpoch !== packStateEpoch
        || userId !== currentUserId
      ) return false;
      commitPackState(state, metadata, userId, { kept });
      return true;
    } catch (err) {
      console.warn('[CardPacks] Failed to load pack state:', err);
      return false;
    }
  }

  /**
   * Install an authoritative pack state (and its opening's metadata) in one
   * task. `kept` is a finished opening a refresh leaves on the stage
   * (finishedPackOnStage): the server reported no pending one.
   */
  function commitPackState(state, metadata, userId, { kept = null } = {}) {
    if (metadata) allCards = metadata.cards;
    packCount = Number.isFinite(state?.packCount) ? state.packCount : 0;
    if (!kept || state?.activePack || kept !== activePack) setActivePack(state?.activePack || null);
    if (Number.isFinite(state?.duplicateValuePerPack)) {
      duplicateValuePerPack = Math.max(1, Math.floor(state.duplicateValuePerPack));
    }
    if (Number.isFinite(state?.normalDuplicateValue)) {
      normalDuplicateValue = Math.max(1, Math.floor(state.normalDuplicateValue));
    }
    if (Number.isFinite(state?.stellarDuplicateValue)) {
      stellarDuplicateValue = Math.max(1, Math.floor(state.stellarDuplicateValue));
    }
    const totals = state?.duplicateTotals;
    if (Number.isFinite(state?.nonCraftableDuplicateValue)) {
      nonCraftableDuplicateValue = Math.max(0, Math.floor(state.nonCraftableDuplicateValue));
    } else if (exchangeVersion === 1 && totals?.ready === true && totals.accountId === userId
      && Number.isSafeInteger(totals.totalValue) && totals.totalValue >= 0) {
      // The maintained total the screen read already carries. It labels the
      // Exchange control from the first frame; only the session's own quote
      // makes it actionable.
      nonCraftableDuplicateValue = totals.totalValue;
      duplicateTotalKnown = true;
    } else {
      updateDuplicateStateFromInventory();
    }
  }

  function currentLanguage() {
    return i18n?.getLanguage?.() || 'en';
  }

  /**
   * One complete read of the Packs screen: config, the account's pack state,
   * and the active opening's own cards. Only the current protocol is read
   * ahead, because only its state names the account it belongs to.
   */
  async function readScreenSnapshot(signal, language) {
    await syncDebugStellarOdds();
    const result = await MoonfallCloud.cardPacks.getScreenState({ signal });
    const state = result?.state;
    if (result?.exchangeVersion !== 1 || result.config?.openingCardVersion !== 1
      || typeof state?.accountId !== 'string' || !state.accountId) return null;
    const [metadata] = await Promise.all([
      readPackOpeningCards({ api: MoonfallCloud.cardPacks, pack: state.activePack,
        accountId: state.accountId, language, signal }),
      loadPackStellarAssets(state.activePack?.items || []),
      holdForPackWrapper(state.activePack),
    ]);
    return { result, metadata, language };
  }

  function snapshotBelongsTo(snapshot, userId) {
    return Boolean(snapshot && userId)
      && snapshot.result?.state?.accountId === userId
      && snapshot.language === currentLanguage();
  }

  function commitScreenSnapshot(snapshot, userId) {
    exchangeVersion = snapshot.result.exchangeVersion;
    applyConfig(snapshot.result.config);
    commitPackState(snapshot.result.state, snapshot.metadata, userId);
  }

  /** The unconsumed warm read that still describes this vault, if any. */
  function usableWarm() {
    const record = screenWarm;
    if (!record || record.consumed || record.mutationEpoch !== packMutationEpoch
      || record.language !== currentLanguage()) return null;
    if (record.settledAt && (!record.snapshot || Date.now() - record.settledAt > SCREEN_WARM_FRESH_MS)) return null;
    return record;
  }

  /**
   * Read the Packs screen ahead of the tab (docs/asset-loading.md law 3):
   * the Collection opening, a pointer or focus on the tab. Best-effort — a
   * failed or foreign read is simply never used, and open() reads again.
   *
   * `wrapperAfter`: what the pack picture waits for, the Collection open's
   * page's own pictures (see warmGenericWrapper). Intent passes nothing.
   * @param {{ wrapperAfter?: PromiseLike<unknown> | null }} [options]
   */
  function warm({ wrapperAfter = null } = {}) {
    warmGenericWrapper(wrapperAfter);
    if (isSurfaceOpen || typeof MoonfallCloud?.cardPacks?.getScreenState !== 'function') {
      return Promise.resolve();
    }
    const usable = usableWarm();
    if (usable) return usable.promise.then(() => undefined);
    screenWarm?.controller.abort();
    const record = {
      controller: new AbortController(),
      language: currentLanguage(),
      mutationEpoch: packMutationEpoch,
      settledAt: 0,
      snapshot: null,
      consumed: false,
      promise: null,
    };
    record.promise = readScreenSnapshot(record.controller.signal, record.language).then((snapshot) => {
      record.snapshot = snapshot;
      record.settledAt = Date.now();
      return snapshot;
    }, () => {
      record.settledAt = Date.now();
      return null;
    });
    screenWarm = record;
    return record.promise.then(() => undefined);
  }

  /**
   * Paint a warmed read synchronously, before the Collection shows the section
   * (MoonExchange.prepare's pattern), so the tab's first frame is its finished
   * frame. open() then consumes the same read and changes nothing.
   */
  function prepare() {
    ensureLayout();
    const section = getSection();
    const warmed = usableWarm();
    const snapshot = warmed?.snapshot;
    preparedWarm = null;
    if (!snapshotBelongsTo(snapshot, currentUserId)) {
      // Not answered yet: this entry is shown once, finished (open() releases).
      if (!isSurfaceOpen) beginEntryHold(section);
      return;
    }
    preparedWarm = warmed;
    commitScreenSnapshot(snapshot, currentUserId);
    packStateResolved = true;
    if (section) delete section.dataset.packsEntering;
    render();
    syncPackStateReadiness();
  }

  /**
   * An entry clears the previous disclosure while current terms are fetched
   * (docs/card-packs.md) — and showing that clear-and-refill would collapse the
   * header by a line and grow it back half a second later, moving the stage
   * and everything on it. So the entry holds everything the read decides out
   * of sight until it lands (deck-builder.css, `data-packs-entering`), which
   * with warm()/prepare() is normally before the first frame. The loading panel
   * appears only past the shared grace; the transport's own timeout bounds it.
   */
  function beginEntryHold(section) {
    stellarOddsKnown = false;
    packConfig.stellarPackOdds = undefined;
    if (section) section.dataset.packsEntering = 'true';
    updateSummary();
  }

  async function loadScreenState(generation, stateEpoch, userId, warmed = null, { keepFinished = false } = {}) {
    try {
      // A warm read is an entry's (warm() refuses while the surface is open);
      // a refresh reads for itself, so it can keep a finished pack.
      const snapshot = warmed && !keepFinished ? await warmed.promise : null;
      if (generation !== openGeneration || stateEpoch !== packStateEpoch || userId !== currentUserId) return false;
      if (snapshotBelongsTo(snapshot, userId)) {
        commitScreenSnapshot(snapshot, userId);
        return true;
      }
      await syncDebugStellarOdds();
      if (generation !== openGeneration) return false;
      const result = await MoonfallCloud.cardPacks.getScreenState({ signal: screenReadController.signal });
      if (generation !== openGeneration || stateEpoch !== packStateEpoch || userId !== currentUserId) return false;
      if (result.exchangeVersion === 1 && result.state?.accountId !== userId) throw new Error('pack_account_changed');
      exchangeVersion = result.exchangeVersion;
      applyConfig(result.config);
      if (openingCardVersion !== 1 && !(await loadLegacyCardContext(generation, userId))) return false;
      return await loadPackState(generation, stateEpoch, userId, result.state, { keepFinished });
    } catch (err) {
      if (generation === openGeneration) console.warn('[CardPacks] Failed to load screen:', err);
      return false;
    }
  }

  /**
   * End this visit's exchange session and everything waiting on it: its last
   * answer, the presses waiting for its quote and the taps held for it.
   */
  function dropExchangeSession() {
    exchangeSession?.close();
    exchangeSession = null;
    exchangeState = null;
    exchangeAnswer = null;
    isConvertingDuplicates = false;
    finishExchangeWaits(false);
    letGoHeldClaims();
  }

  /**
   * (Re)start the session. Its last answer is kept: the re-quote on a refresh
   * or after a claim is looked up against what the player already saw.
   */
  function startExchangeSession() {
    exchangeSession?.close();
    exchangeSession = null;
    exchangeState = null;
    if (exchangeVersion !== 1 || !currentUserId || !isSurfaceOpen) {
      finishExchangeWaits(false);
      letGoHeldClaims();
      return;
    }
    // Owned by identity, not by the open generation: a refresh keeps the
    // session reporting until its read lands and restarts it.
    const owner = contextGeneration;
    let session = null;
    const current = () => isSurfaceOpen && owner === contextGeneration && exchangeSession === session;
    let storage;
    try { storage = window.localStorage; } catch (_) { /* Server recovery still works. */ }
    session = exchangeSession = createPackExchangeSession({
      api: MoonfallCloud.cardPacks.createExchangeSessionApi?.() || MoonfallCloud.cardPacks,
      source: MoonfallCloud.deploymentUrl(), storage,
      onCommitted: (_operation, accountId) => {
        if (!current()) return;
        // Receipts contain historical quantities. Mark dependent views stale;
        // they must read their current inventory when entered, never debit it
        // again because this screen reconnected or resumed an operation.
        onInventoryInvalidated?.(accountId);
      },
      onState: state => {
        if (!current() || state.accountId !== currentUserId) return;
        if (state.phase === 'error') notePackRegionRefusal(state.error);
        exchangeState = state;
        if (!EXCHANGE_QUOTING.includes(state.phase)) exchangeAnswer = state;
        /* Only a running exchange holds Buy, Charge and the claims — and a
           quote after an answer that was running, which is that exchange being
           looked up again. Found 25 September 2026 (flicker hunt): holding them
           for every quote greyed Buy and Charge for the round trip on every
           entry and every reveal, and dropped a card tap made in that window
           without a press, a sound or a reveal. */
        isConvertingDuplicates = EXCHANGE_RUNNING.includes(state.phase)
          || (EXCHANGE_QUOTING.includes(state.phase) && EXCHANGE_RUNNING.includes(exchangeAnswer?.phase));
        if (state.quote?.ready) {
          nonCraftableDuplicateValue = state.quote.totalValue;
          duplicateTotalKnown = true;
        }
        if (state.operation) {
          packCount = state.operation.packCount;
          // An exchange moves packs; a screen read from before it is stale —
          // a warm one, and a refresh's, which keeps this session reporting.
          packMutationEpoch += 1;
          packStateEpoch += 1;
        }
        getSection()?.setAttribute('data-exchange-state', state.phase);
        renderExchangeStatus();
        // Progress only touches the counts and controls. Never rebuild the
        // opening's card nodes or restart its ceremony on a poll.
        updateSummary();
        settleExchangeWaits();
      },
    });
    void session.open(currentUserId);
  }

  function exchangeSettledNow() {
    return pendingClaims.size === 0 && Boolean(exchangeState)
      && !EXCHANGE_QUOTING.includes(exchangeState.phase);
  }

  /**
   * An Exchange press made while a quote is counted (or a claim is in flight,
   * which a quote follows) is kept, not refused: it resolves true once the
   * session has answered, false if the session or the surface went away.
   * Inside the grace the pressed control keeps its look; past it, it says it
   * is counting (updateDuplicatesUI).
   */
  function exchangeSettled() {
    if (exchangeSettledNow()) return Promise.resolve(true);
    if (!exchangeSession) return Promise.resolve(false);
    return new Promise((resolve) => {
      const grace = setTimeout(() => {
        exchangePressWaiting = true;
        updateDuplicatesUI();
      }, PACK_ART_GRACE_MS);
      exchangeWaiters.add((answered) => {
        clearTimeout(grace);
        if (exchangePressWaiting) {
          exchangePressWaiting = false;
          updateDuplicatesUI();
        }
        resolve(answered);
      });
      // The click guard greyed the control on the press; the guard alone
      // refuses a second one, so nothing needs to look busy yet.
      updateDuplicatesUI();
    });
  }

  function finishExchangeWaits(answered) {
    const waiters = Array.from(exchangeWaiters);
    exchangeWaiters.clear();
    waiters.forEach((finish) => finish(answered));
  }

  /** The session answered (or a claim settled): release what waited on it. */
  function settleExchangeWaits() {
    releaseHeldClaims();
    if (exchangeWaiters.size > 0 && exchangeSettledNow()) finishExchangeWaits(true);
  }

  // Held taps are claimed only while no exchange may be running under them:
  // before this visit's session has answered, a claim could change the
  // collection under another tab's exchange.
  function claimWaitsForExchange() {
    return exchangeVersion === 1 && EXCHANGE_QUOTING.includes(exchangeState?.phase)
      && !EXCHANGE_IDLE.includes(exchangeAnswer?.phase);
  }

  function packCardAt(index) {
    return document.querySelector(`#card-packs-cards .card-pack-card[data-index="${index}"]`);
  }

  /** Hold a tap on its card, acknowledged like any press, until the answer. */
  function holdClaim(index) {
    if (heldClaims.has(index)) return;
    heldClaims.set(index, { packId: activePack?.id, context: contextGeneration });
    setPackCardCharging(packCardAt(index), true, { pressed: true });
    playSound('press');
  }

  function releaseHeldClaims() {
    if (heldClaims.size === 0 || claimWaitsForExchange()) return;
    const taps = Array.from(heldClaims);
    heldClaims.clear();
    taps.forEach(([index, tap]) => {
      const item = tap.context === contextGeneration && activePack?.id === tap.packId
        ? activePack.items?.[index] : null;
      if (item && !item.claimed && !isConvertingDuplicates) {
        void handleClaimCard(index, { acknowledged: true });
      } else if (item?.claimed) {
        // Claimed meanwhile (another tab): its reveal owns the charge.
        packCardAt(index)?.classList.remove('card-pack-card-pressed');
      } else {
        // A running exchange answered, or the pack changed: let the tap go.
        setPackCardCharging(packCardAt(index), false);
      }
    });
  }

  function letGoHeldClaims() {
    heldClaims.forEach((_tap, index) => setPackCardCharging(packCardAt(index), false));
    heldClaims.clear();
  }

  function renderExchangeStatus() {
    const state = exchangeState;
    if (!state) return;
    const count = state.operation?.packsGranted ?? 0;
    const exchangeStatus = (message, tone) => setStatus(message, tone, 'exchange');
    if (state.phase === 'loading' || state.phase === 'preparing' || state.phase === 'ready') {
      // Quiet bookkeeping: the session re-quotes on every entry and after every
      // claim, and the Exchange control already says it is counting. Writing
      // that into the status line replaced "Claimed. Choose another." and then
      // blanked it — text churn the player never asked for. Clear only a
      // message this session wrote.
      if (statusOwner === 'exchange') exchangeStatus('');
    } else if (state.phase === 'reconnecting') {
      exchangeStatus(uiText('collection.packs.exchangeReconnecting', 'Reconnecting… Your confirmed exchange will resume.'));
    } else if (state.phase === 'exchanging') {
      exchangeStatus(state.operation
        ? uiText('collection.packs.exchangeProgress', 'Packs added: {{count}}/{{total}}.', { count, total: state.operation.targetValue / 20 })
        : uiText('collection.packs.converting', 'Exchanging extra copies...'));
    } else if (state.phase === 'completed') {
      exchangeStatus(uiText('collection.packs.exchangeCompleted', 'Packs added: {{count}}. Exchange complete.', { count }), 'success');
    } else if (state.phase === 'interrupted') {
      exchangeStatus(state.operation.reason === 'account_banned'
        ? uiText('collection.packs.exchangeBlocked', 'Packs added: {{count}}. This account can no longer exchange cards.', { count })
        : uiText('collection.packs.exchangeInterrupted', 'Packs added: {{count}}. Your collection changed; review the new offer to continue.', { count }), 'error');
    } else if (state.phase === 'quote-changed') {
      exchangeStatus(uiText('collection.packs.exchangeQuoteChanged', 'Your collection changed. Review the new offer.'), 'error');
    } else if (state.phase === 'error') {
      exchangeStatus(playerFacingError(state.error, uiText('collection.packs.convertFailed', 'Failed to convert duplicates.')), 'error');
    } else if (statusOwner === 'exchange') {
      exchangeStatus('');
    }
  }

  // Record the tear so the opening survives a refresh, a re-entry, or a
  // reload. Best-effort: the pack is already charged and paid for, so a failed
  // write costs nothing but the memory of the tear.
  function rememberPackOpened(packId) {
    if (!packId) return;
    locallyTornPackId = packId;
    storeSessionTornPackId(packId);
    const mark = MoonfallCloud?.cardPacks?.markPackOpened;
    const pendingKey = `${currentUserId || 'anonymous'}:${packId}`;
    if (typeof mark !== 'function' || pendingOpenedMarks.has(pendingKey)) return;
    pendingOpenedMarks.add(pendingKey);
    let request;
    try {
      request = mark(packId);
    } catch (err) {
      pendingOpenedMarks.delete(pendingKey);
      console.warn('[CardPacks] Failed to record the opening:', err);
      return;
    }
    Promise.resolve(request)
      .catch((err) => {
        console.warn('[CardPacks] Failed to record the opening:', err);
      })
      .finally(() => {
        pendingOpenedMarks.delete(pendingKey);
      });
  }

  function prefersReducedMotion() {
    return typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  const SHARD_COUNT = 30;
  // The seam is vertical, so the foil leaves it sideways. Shards are thrown
  // into a cone either side of the rip rather than evenly around a circle —
  // an even circle is what made the old shared burst read as a firework.
  const SHARD_CONE = 1.02;
  // Two foils in the wrapper: the pale gold of the pack and the crimson of the
  // ribbon. Mixing them is what stops the debris reading as generic sparkle.
  const SHARD_TONES = [
    ['rgba(255, 250, 226, 0.98)', 'rgba(214, 173, 78, 0.92)'],
    ['rgba(255, 244, 206, 0.96)', 'rgba(180, 138, 54, 0.9)'],
    ['rgba(255, 236, 232, 0.95)', 'rgba(140, 47, 63, 0.92)'],
  ];

  /**
   * Throw torn foil out of the rip. Presentation only, and entirely optional:
   * the pack is already committed by the time this runs.
   *
   * Every shard is randomised independently — angle inside the cone, distance,
   * size, spin direction and rate, birth delay and lifetime — because identical
   * particles moving in lockstep is exactly what a burst must not look like.
   * The arc (a mid-flight apex plus a fall) is in CSS; JS only hands each shard
   * its numbers, so there is no per-frame work at the one moment in this
   * surface that must not drop a frame.
   */
  function spawnFoilShards(packEl, stageEl) {
    const host = stageEl || packEl?.parentElement;
    if (!host || !packEl || typeof document === 'undefined') return;
    clearFoilShards();

    const burst = document.createElement('div');
    burst.className = 'card-packs-foil-burst';
    // The rip, in the stage's own coordinates — layout values, so the pack's
    // float and drag scale cannot displace the origin.
    burst.style.left = `${packEl.offsetLeft + packEl.offsetWidth / 2}px`;
    burst.style.top = `${packEl.offsetTop + packEl.offsetHeight * 0.46}px`;

    const reach = Math.max(uiPx(60), packEl.offsetWidth || uiPx(150));
    const rand = (min, max) => min + Math.random() * (max - min);
    for (let i = 0; i < SHARD_COUNT; i += 1) {
      const toLeft = i % 2 === 0;
      // Centred on horizontal, biased upward: debris leaves a rip outward and
      // slightly up, then gravity takes it.
      const angle = rand(-SHARD_CONE, SHARD_CONE) / 2 - 0.34;
      const distance = reach * rand(0.34, 1.32);
      const [hi, lo] = SHARD_TONES[Math.floor(Math.random() * SHARD_TONES.length)];
      const shard = document.createElement('span');
      shard.className = 'card-packs-foil-shard';
      shard.style.setProperty('--sx', `${(toLeft ? -1 : 1) * Math.cos(angle) * distance}px`);
      shard.style.setProperty('--sy', `${Math.sin(angle) * distance}px`);
      shard.style.setProperty('--fall', `${rand(0.5, 1.25) * reach}px`);
      shard.style.setProperty('--spin', `${(Math.random() < 0.5 ? -1 : 1) * rand(160, 900)}deg`);
      shard.style.setProperty('--shard-w', `${(rand(2, 4.6) * uiPx(1)).toFixed(1)}px`);
      shard.style.setProperty('--shard-h', `${(rand(5, 15) * uiPx(1)).toFixed(1)}px`);
      shard.style.setProperty('--shard-hi', hi);
      shard.style.setProperty('--shard-lo', lo);
      // Staggered birth AND staggered death: a burst whose particles all expire
      // together reads as a single object vanishing.
      shard.style.setProperty('--shard-delay', `${Math.round(rand(0, 150))}ms`);
      shard.style.setProperty('--shard-dur', `${Math.round(rand(430, 940))}ms`);
      burst.appendChild(shard);
    }

    host.appendChild(burst);
    foilBurstEl = burst;
    foilBurstTimer = setTimeout(clearFoilShards, 1300);
  }

  function clearFoilShards() {
    if (foilBurstTimer) {
      clearTimeout(foilBurstTimer);
      foilBurstTimer = null;
    }
    foilBurstEl?.remove();
    foilBurstEl = null;
  }

  function clearTearPresentation() {
    if (packTearTimer) {
      clearTimeout(packTearTimer);
      packTearTimer = null;
    }
    if (stageTearTimer) {
      clearTimeout(stageTearTimer);
      stageTearTimer = null;
    }
    document.getElementById('card-packs-pack')
      ?.classList.remove('card-packs-pack-tearing', 'card-packs-pack-straining');
    document.getElementById('card-packs-stage')
      ?.classList.remove('card-packs-stage-tearing');
    clearSealNudge();
  }

  function openPackReveal() {
    if (!activePack || activePack.opened) return;
    clearTearPresentation();
    activePack.opened = true;
    tearLeadInPending = !prefersReducedMotion();
    rememberPackOpened(activePack.id);
    // The tear moment: the pack rips, the stage answers it, and foil goes
    // everywhere. Presentation only — `activePack` is already committed above,
    // so a browser that draws none of this still opens the same pack.
    const packEl = document.getElementById('card-packs-pack');
    const stageEl = document.getElementById('card-packs-stage');
    if (packEl) {
      packEl.classList.add('card-packs-pack-tearing');
      packEl.classList.remove('card-packs-pack-straining');
      if (!prefersReducedMotion()) spawnFoilShards(packEl, stageEl);
      // The tear is a one-shot moment; the opened rest-state takes over after.
      packTearTimer = setTimeout(() => {
        packTearTimer = null;
        packEl.classList.remove('card-packs-pack-tearing');
      }, 700);
    }
    if (stageEl) {
      stageEl.classList.add('card-packs-stage-tearing');
      stageTearTimer = setTimeout(() => {
        stageTearTimer = null;
        stageEl.classList.remove('card-packs-stage-tearing');
      }, 760);
    }
    // The seal is foil, so the tear sounds like foil. Borrowing the Foundry's
    // 'magic' chime made the most physical moment in the game sound like a spell.
    // Two layers, not two effects: the transient gives the rip its edge and the
    // foil clip carries the body behind it.
    playSound('impact');
    playSound('pack_open');
    /* The claims are seconds away — re-warm their cues NOW so Firefox's
       suspended decoders are awake before the first card flips. Best-effort
       per the asset-loading doctrine: never blocks, never changes behavior. */
    try { void SoundEffects?.preload?.(['card_reveal', 'stellar']); } catch (_) { }
    setStatus(uiText('collection.packs.chooseCard', 'Choose a card.'));
    render();
  }

  // A physical click may not tear the seal — opening demands a real drag.
  // The nudge tells the player the pack is alive and how to open it.
  function clearSealNudge() {
    if (sealNudgeTimer) {
      clearTimeout(sealNudgeTimer);
      sealNudgeTimer = null;
    }
    document.getElementById('card-packs-pack')
      ?.classList.remove('card-packs-pack-nudge');
    document.getElementById('card-packs-pack-hint')
      ?.classList.remove('card-packs-pack-hint-emphasis');
  }

  function nudgeSealHint() {
    const packEl = document.getElementById('card-packs-pack');
    const hintEl = document.getElementById('card-packs-pack-hint');
    if (!packEl) return;
    packEl.classList.remove('card-packs-pack-nudge');
    void packEl.offsetWidth; // restart the animation on repeated clicks
    packEl.classList.add('card-packs-pack-nudge');
    hintEl?.classList.add('card-packs-pack-hint-emphasis');
    if (sealNudgeTimer) clearTimeout(sealNudgeTimer);
    sealNudgeTimer = setTimeout(() => {
      sealNudgeTimer = null;
      packEl.classList.remove('card-packs-pack-nudge');
      hintEl?.classList.remove('card-packs-pack-hint-emphasis');
    }, 700);
  }

  function hasFinePointer() {
    return typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: fine)').matches;
  }

  function supportsWholePackPointer() {
    return hasFinePointer()
      && !window.matchMedia(COMPACT_PHONE_MEDIA_QUERY).matches;
  }

  function isOutsideSeal(event) {
    const seal = document.getElementById('card-packs-pack-seal');
    return !seal || !seal.contains(event?.target);
  }

  function calculateSealDragThreshold(packEl) {
    const rectHeight = Number(packEl?.getBoundingClientRect?.().height) || 0;
    const renderedHeight = rectHeight || Number(packEl?.offsetHeight) || uiPx(320);
    return Math.min(
      uiPx(SEAL_DRAG_MAX_PX),
      Math.max(uiPx(SEAL_DRAG_MIN_PX), renderedHeight * SEAL_DRAG_PACK_RATIO)
    );
  }

  function setSealStretch(sealEl, { stretch = 0, sway = 0 } = {}) {
    if (!sealEl) return;
    const safeStretch = Math.round(Math.max(0, Number(stretch) || 0) * 1000) / 1000;
    // Sway is pointer travel in screen pixels: its cap grows with the screen,
    // and the lean is read in full-HD pixels so the seal tips the same angle.
    const swayCap = uiPx(14);
    const safeSway = Math.max(-swayCap, Math.min(swayCap, Number(sway) || 0));
    sealEl.style.setProperty('--seal-stretch', `${safeStretch}px`);
    sealEl.style.setProperty('--seal-sway', `${safeSway}px`);
    sealEl.style.setProperty('--seal-rot', `${Math.round((safeSway / uiPx(1)) * 25) / 100}deg`);
  }

  function resetSealDrag({ releaseCapture = true } = {}) {
    const state = dragState;
    dragState = null;
    const packEl = state?.packEl || document.getElementById('card-packs-pack');
    packEl?.classList.remove('card-packs-pack-dragging');
    packEl?.classList.remove('card-packs-pack-straining');
    packEl?.style.setProperty('--pack-open-progress', '0');
    setSealStretch(state?.sealEl || document.getElementById('card-packs-pack-seal'));
    if (!releaseCapture || !state?.sealEl || state.pointerId == null) return;
    try {
      if (!state.sealEl.hasPointerCapture || state.sealEl.hasPointerCapture(state.pointerId)) {
        state.sealEl.releasePointerCapture(state.pointerId);
      }
    } catch (_) {
      // Pointer capture can already be gone after cancellation or DOM teardown.
    }
  }

  function handleSealPointerDown(event) {
    if (!activePack || activePack.opened) return;
    if (dragState || event.isPrimary === false) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const packEl = document.getElementById('card-packs-pack');
    if (!packEl) return;

    dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      packEl,
      sealEl: event.currentTarget,
      threshold: calculateSealDragThreshold(packEl),
      progress: 0,
    };
    packEl.classList.add('card-packs-pack-dragging');
    setSealStretch(event.currentTarget);
    event.preventDefault();

    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (_) {
      // Ignore pointer capture failures.
    }
  }

  function handleSealPointerMove(event) {
    if (!dragState || !activePack || activePack.opened) return;
    if (event.pointerId !== dragState.pointerId) return;
    // The R1 ribbon pulls DOWNWARD: the travel elongates the ribbon itself
    // while it sways gently toward the pointer, like real fabric.
    const travel = Math.max(0, event.clientY - dragState.startY);
    const progress = Math.min(1, travel / dragState.threshold);
    dragState.progress = progress;
    setSealStretch(dragState.sealEl, {
      stretch: Math.min(travel, dragState.threshold),
      sway: (event.clientX - dragState.startX) * 0.25,
    });
    // Drives the CSS build-up (glow/strain) toward the tear.
    dragState.packEl.style.setProperty('--pack-open-progress', String(Math.round(progress * 1000) / 1000));
    // ...and the last of that travel shivers, so the tear is felt coming.
    dragState.packEl.classList.toggle(
      'card-packs-pack-straining',
      progress >= SEAL_STRAIN_PROGRESS,
    );
    event.preventDefault();
    if (progress >= 1) {
      resetSealDrag();
      openPackReveal();
    }
  }

  function handleSealPointerEnd(event) {
    if (!dragState) return;
    if (event.pointerId !== dragState.pointerId) return;
    resetSealDrag({ releaseCapture: event.type !== 'lostpointercapture' });
  }

  function handleSealClick(event) {
    // Keyboard activation (event.detail === 0) stays the accessible path; a
    // physical click never opens the pack — the seal must be dragged.
    if (event.detail !== 0) {
      event.preventDefault();
      nudgeSealHint();
      return;
    }
    openPackReveal();
  }

  function handlePackPointerDown(event) {
    if (!supportsWholePackPointer() || !isOutsideSeal(event)) return;
    handleSealPointerDown(event);
  }

  function handlePackPointerMove(event) {
    if (!dragState || dragState.sealEl !== event.currentTarget) return;
    if (!supportsWholePackPointer()) {
      resetSealDrag();
      return;
    }
    handleSealPointerMove(event);
  }

  function handlePackPointerEnd(event) {
    if (!dragState || dragState.sealEl !== event.currentTarget) return;
    handleSealPointerEnd(event);
  }

  function handlePackClick(event) {
    if (!supportsWholePackPointer() || !isOutsideSeal(event) || event.button !== 0) return;
    nudgeSealHint();
  }

  function handleSealKeyDown(event) {
    if (event.repeat || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    openPackReveal();
  }

  /* The page's one region read (shared with the Store and the consent gate),
     started at init so the vault's first paint already knows. A failed read
     leaves packs offered: the server is the gate, and refuses in Brazil. */
  function readPackRegion() {
    if (!packRegionRead) {
      packRegionRead = Promise.resolve(RegionAvailability?.getRegionAvailability?.())
        .then((region) => { packsUnavailableInRegion = region?.packsBlocked === true; })
        .catch(() => {});
    }
    return packRegionRead;
  }

  // The server said so: the vault stops offering what it refuses.
  function notePackRegionRefusal(err) {
    if (errorCode(err) === 'packs_unavailable_in_region') packsUnavailableInRegion = true;
  }

  async function handleBuyPack() {
    if (!MoonfallCloud?.cardPacks?.buyPack) return;
    if (isPackStateLoading() || isConvertingDuplicates || packsUnavailableInRegion) return;
    if (!updateOdds()) return;
    if (stardust < packConfig.packPrice) {
      setStatus(uiText('collection.packs.notEnoughStardust', 'Not enough Stardust.'), 'error');
      return;
    }

    const mutation = beginPackStateMutation();
    const buyBtn = document.getElementById('card-packs-buy-btn');
    if (buyBtn) buyBtn.disabled = true;
    /* THE VAULT IS WAITING FOR ITS ANSWER. The count, the hint and the status
       change together one round trip after the press; `aria-busy` says so —
       to a screen reader (which then reads the result, not the interim line)
       and to the snappy meter, which judges that answer as the press's one
       loading state rather than text changing under the player. */
    const vault = getSection();
    vault?.setAttribute('aria-busy', 'true');
    setStatus(uiText('collection.packs.purchasing', 'Purchasing pack...'));
    isLoading = true;

    try {
      const result = await MoonfallCloud.cardPacks.buyPack(1);
      checkpointPackStateMutation(mutation);
      if (!ownsMutationContext(mutation)) return;
      if (Number.isFinite(result?.packCount)) packCount = result.packCount;
      if (Number.isFinite(result?.stardust)) {
        stardust = result.stardust;
        if (typeof onStardustUpdate === 'function') {
          onStardustUpdate(stardust);
        }
      }
      setStatus(uiText('collection.packs.secured', 'Pack secured.'), 'success');
      if (buyBtn) FX?.burst?.(buyBtn);
    } catch (err) {
      if (!ownsMutationContext(mutation)) return;
      console.error('[CardPacks] Failed to buy pack:', err);
      notePackRegionRefusal(err);
      setStatus(playerFacingError(err, uiText('collection.packs.buyFailed', 'Failed to buy pack.')), 'error');
    } finally {
      finishPackStateMutation(mutation);
      if (ownsMutationContext(mutation)) {
        isLoading = false;
        if (buyBtn) buyBtn.disabled = false;
        render();
      }
      // In the same task as the answer's render: busy ends as it lands.
      vault?.removeAttribute('aria-busy');
    }
  }

  async function handleConvertDuplicates() {
    if (isPackStateLoading() || isLoading || isOpening || packsUnavailableInRegion) return;
    if (exchangeVersion === 1 && exchangeState?.phase === 'error') { startExchangeSession(); return; }
    if (exchangeVersion === 1) {
      // Confirmed only against the session's own current quote: a press made
      // while it is counted waits for it rather than being dropped.
      const waitContext = contextGeneration;
      if (!(await exchangeSettled()) || waitContext !== contextGeneration) return;
    }
    if (isPackStateLoading() || isLoading || isOpening || pendingClaims.size > 0) return;
    if (isConvertingDuplicates) return;
    if (exchangeVersion !== 1 && !MoonfallCloud?.cardPacks?.convertNonCraftableDuplicates) return;
    const confirmationContext = contextGeneration;
    const confirmationGeneration = openGeneration;
    const confirmedQuote = exchangeVersion === 1 ? exchangeState?.quote : null;
    if (exchangeVersion === 1 && !confirmedQuote?.ready) return;
    const perPack = Number.isFinite(duplicateValuePerPack) && duplicateValuePerPack > 0
      ? Math.floor(duplicateValuePerPack)
      : 20;
    const availablePacks = perPack > 0 ? Math.floor(nonCraftableDuplicateValue / perPack) : 0;
    if (availablePacks <= 0) {
      setStatus(uiText('collection.packs.needDuplicates', 'Need more duplicate value.'), 'error');
      return;
    }

    const valueConsumed = availablePacks * perPack;
    const confirmation = availablePacks === 1
      ? uiText(
        'collection.packs.confirmConvertOne',
        'Exchange extra cards worth {{value}} for 1 pack? This cannot be undone.',
        { value: valueConsumed }
      )
      : uiText(
        'collection.packs.confirmConvertMany',
        'Exchange extra cards worth {{value}} for {{count}} packs? This cannot be undone.',
        { value: valueConsumed, count: availablePacks }
      );
    if (!await Confirm.ask(confirmation, {
      /* The exchange rules, at the moment they matter. They used to live as a
         permanent paragraph in the pack header, where they were three lines of
         fine print nobody was reading and everybody was looking at. */
      detail: uiText(
        'collection.packs.duplicatesNote',
        'Exchange only extra copies from card packs. Crafted cards are excluded and never discarded. One Normal and one Stellar copy of each pack card are kept. Each extra Normal is worth {{normalValue}}; each extra Stellar is worth {{stellarValue}}.',
        { normalValue: normalDuplicateValue, stellarValue: stellarDuplicateValue },
      ),
      // The message pluralizes but this label did not: exchanging 40 value
      // for 2 packs offered a button reading "Exchange for 1 pack". The
      // count-aware key already exists and is translated in every language.
      confirmLabel: availablePacks === 1
        ? uiText('collection.packs.convertToPack', 'Exchange for 1 pack')
        : uiText('collection.packs.convertPacks', 'Exchange for {{count}} packs', { count: availablePacks }),
    })) return;
    if (confirmationContext !== contextGeneration || confirmationGeneration !== openGeneration
      || isPackStateLoading() || isLoading || isOpening || isConvertingDuplicates || pendingClaims.size > 0) return;

    if (exchangeVersion === 1) {
      await exchangeSession?.confirm(confirmedQuote);
      return;
    }

    const mutation = beginPackStateMutation();
    const convertBtn = document.getElementById('card-packs-duplicates-btn');
    if (convertBtn) convertBtn.disabled = true;
    isConvertingDuplicates = true;
    setStatus(uiText('collection.packs.converting', 'Exchanging extra copies...'));

    try {
      const result = await MoonfallCloud.cardPacks.convertNonCraftableDuplicates();
      checkpointPackStateMutation(mutation);
      if (!ownsMutationContext(mutation)) return;
      const consumedCards = Array.isArray(result?.consumedCards) ? result.consumedCards : [];
      consumedCards.forEach((entry) => {
        const cardId = entry?.cardId;
        const qty = Math.floor(Number(entry?.quantity ?? 0));
        if (!cardId || !Number.isFinite(qty) || qty <= 0) return;
        const variant = entry?.isStellar ? 'stellar' : 'normal';
        applyInventoryDelta(cardId, variant, -qty, false);
      });
      if (Number.isFinite(result?.packCount)) packCount = result.packCount;
      if (Number.isFinite(result?.duplicateValuePerPack)) {
        duplicateValuePerPack = Math.max(1, Math.floor(result.duplicateValuePerPack));
      }
      if (Number.isFinite(result?.normalDuplicateValue)) {
        normalDuplicateValue = Math.max(1, Math.floor(result.normalDuplicateValue));
      }
      if (Number.isFinite(result?.stellarDuplicateValue)) {
        stellarDuplicateValue = Math.max(1, Math.floor(result.stellarDuplicateValue));
      }
      if (Number.isFinite(result?.nonCraftableDuplicateValue)) {
        nonCraftableDuplicateValue = Math.max(0, Math.floor(result.nonCraftableDuplicateValue));
      } else {
        updateDuplicateStateFromInventory();
      }
      setStatus(uiText('collection.packs.added', 'Exchange complete.'), 'success');
    } catch (err) {
      if (!ownsMutationContext(mutation)) return;
      console.error('[CardPacks] Failed to convert duplicates:', err);
      notePackRegionRefusal(err);
      setStatus(playerFacingError(err, uiText('collection.packs.convertFailed', 'Failed to convert duplicates.')), 'error');
    } finally {
      finishPackStateMutation(mutation);
      if (ownsMutationContext(mutation)) {
        isConvertingDuplicates = false;
        if (convertBtn) convertBtn.disabled = false;
        render();
      }
    }
  }

  async function handleOpenPack() {
    if (!MoonfallCloud?.cardPacks?.openPack) return;
    if (isPackStateLoading() || isOpening || isConvertingDuplicates || packsUnavailableInRegion) return;
    if (activePack && !activePack.complete) {
      setStatus(uiText('collection.packs.finishCurrent', 'Finish opening your current pack first.'));
      return;
    }
    if (packCount <= 0) {
      setStatus(uiText('collection.packs.noneAvailable', 'No packs available.'), 'error');
      return;
    }

    const mutation = beginPackStateMutation();
    const generation = openGeneration;
    let openingCommitted = false;
    const openBtn = document.getElementById('card-packs-open-btn');
    if (openBtn) openBtn.disabled = true;
    // The vault waits for the charge's answer as it does for a purchase's
    // (handleBuyPack): the count, the hint moving under the pack and the
    // status all change with it.
    const vault = getSection();
    vault?.setAttribute('aria-busy', 'true');
    setStatus(uiText('collection.packs.charging', 'Charging pack...'));
    isOpening = true;

    try {
      const result = await MoonfallCloud.cardPacks.openPack();
      openingCommitted = true;
      checkpointPackStateMutation(mutation);
      if (!ownsMutationContext(mutation)) return;
      if (MoonfallAnalytics) {
        MoonfallAnalytics.capture('pack_opened', { packSize: Array.isArray(result?.items) ? result.items.length : undefined });
      }
      if (Number.isFinite(result?.packCount)) packCount = result.packCount;
      const nextPack = {
        id: result?.packId,
        collection: result?.collection || null,
        items: Array.isArray(result?.items) ? result.items : [],
      };
      if (!isSurfaceOpen || generation !== openGeneration) {
        if (isSurfaceOpen) void open();
        return;
      }
      // The vault is already busy on this answer: the pack arrives once its
      // faces are described and its wrapper is decoded, whole, in one task.
      const [metadata] = await Promise.all([
        openingCardVersion === 1 ? readPackOpeningCards({ api: MoonfallCloud.cardPacks,
          pack: nextPack, accountId: currentUserId, language: i18n?.getLanguage?.() || 'en',
          signal: screenReadController?.signal }) : null,
        loadPackStellarAssets(nextPack.items),
        holdForPackWrapper(nextPack),
      ]);
      if (!ownsMutationContext(mutation)) return;
      if (generation !== openGeneration) { if (isSurfaceOpen) void open(); return; }
      if (metadata) allCards = metadata.cards;
      setActivePack(nextPack);
      if (result?.cardbackUnlocked) pendingCardbackUnlockPackId = nextPack.id;
      playPackChargeBeat();
      setStatus(uiText('collection.packs.charged', 'Pack charged. Break the seal.'), 'success');
    } catch (err) {
      if (!ownsMutationContext(mutation)) return;
      if (generation !== openGeneration) return;
      if (openingCommitted) {
        // The server may already own a pending opening. A metadata failure is
        // not permission to charge another pack before reconciling on reentry.
        packStateResolved = false;
        const section = getSection();
        if (section) section.dataset.packsLoadState = 'error';
      }
      console.error('[CardPacks] Failed to open pack:', err);
      notePackRegionRefusal(err);
      setStatus(playerFacingError(err, uiText('collection.packs.openFailed', 'Failed to open pack.')), 'error');
    } finally {
      finishPackStateMutation(mutation);
      if (ownsMutationContext(mutation)) {
        isOpening = false;
        if (openBtn) openBtn.disabled = false;
        render();
      }
      vault?.removeAttribute('aria-busy');
    }
  }

  async function handleClaimCard(index, { acknowledged = false } = {}) {
    if (!MoonfallCloud?.cardPacks?.claimPackCard) return;
    if (isConvertingDuplicates) return;
    if (!activePack || !activePack.opened) return;
    const claimPackId = activePack.id;
    const claimGeneration = openGeneration;
    const claimUserId = currentUserId;
    const pendingClaimKey = `${claimUserId || 'anonymous'}:${claimPackId}:${index}`;
    if (pendingClaims.has(pendingClaimKey)) return;

    const item = activePack.items[index];
    if (!item || item.claimed) return;
    if (claimWaitsForExchange()) {
      holdClaim(index);
      return;
    }

    pendingClaims.add(pendingClaimKey);
    const mutation = beginPackStateMutation();
    setStatus(uiText('collection.packs.revealing', 'Revealing...'));
    const cardWrapper = document.querySelector(`#card-packs-cards .card-pack-card[data-index="${index}"]`);
    // The press lands on the card in this same frame; its face is made ready
    // behind its back while the claim travels.
    setPackCardCharging(cardWrapper, true, { pressed: true });
    if (cardWrapper) preparePressedPackFace(cardWrapper, index, pendingClaimKey);

    const claimKey = `cardPacks.claim:${claimPackId}:${index}`;
    const runClaim = () => MoonfallCloud.cardPacks.claimPackCard(claimPackId, index);
    const invoker = MoonfallCloudGuard
      ? () => MoonfallCloudGuard.run(claimKey, runClaim)
      : runClaim;
    let renderCurrentPack = false;
    let refreshReenteredPack = false;

    // Immediate physical acknowledgement only. The reward cue remains behind
    // the authoritative result below and never implies an unaccepted claim.
    // A held tap was acknowledged when it landed (holdClaim).
    if (!acknowledged) playSound('press');
    try {
      const result = await invoker();
      checkpointPackStateMutation(mutation);
      if (!ownsMutationContext(mutation) || claimUserId !== currentUserId) return;
      const variant = result?.isStellar ? 'stellar' : 'normal';
      if (exchangeVersion === 1) onInventoryInvalidated?.(claimUserId);
      else applyInventoryDelta(result?.cardId || item.cardId, variant, 1);
      const section = getSection();
      const presentationIsCurrent = claimGeneration === openGeneration
        && activePack?.id === claimPackId
        && section?.dataset.packsLoadState !== 'idle';
      if (!presentationIsCurrent) {
        // The accepted server result still updates inventory above. Never
        // project an old pack's item/complete state onto a newer pack or a
        // closed section. If the player re-entered the SAME pack while the
        // claim was in flight, refresh that pack from the server instead of
        // replaying this stale presentation snapshot.
        refreshReenteredPack = activePack?.id === claimPackId
          && section?.dataset.packsLoadState !== 'idle';
        return;
      }

      pendingRevealCues.set(`${claimPackId}:${index}`, {
        packGeneration: claimGeneration,
        packId: claimPackId,
        cardId: item.cardId,
        isStellar: result?.isStellar === true || item.isStellar === true,
      });
      activePack.items[index] = {
        cardId: item.cardId,
        isStellar: item.isStellar === true,
        claimed: true,
      };

      if (result?.packComplete) {
        activePack.complete = true;
        setStatus(uiText('collection.packs.packComplete', 'Pack complete.'), 'success');
        drainPendingCardbackUnlock(claimPackId);
      } else {
        setStatus(uiText('collection.packs.claimed', 'Claimed. Choose another.'), 'success');
      }
      renderCurrentPack = true;
    } catch (err) {
      if (!ownsMutationContext(mutation) || claimUserId !== currentUserId) return;
      console.error('[CardPacks] Failed to claim card:', err);
      const section = getSection();
      if (claimGeneration === openGeneration
          && activePack?.id === claimPackId
          && section?.dataset.packsLoadState !== 'idle') {
        setStatus(playerFacingError(err, uiText('collection.packs.claimFailed', 'Failed to claim card.')), 'error');
        renderCurrentPack = true;
      }
    } finally {
      finishPackStateMutation(mutation);
      pendingClaims.delete(pendingClaimKey);
      if (!ownsMutationContext(mutation)) {
        return;
      }
      if (refreshReenteredPack) {
        // A fresh open generation supersedes any re-entry query whose server
        // snapshot was taken before this accepted claim committed.
        await open();
      } else if (renderCurrentPack) {
        render();
        if (exchangeVersion === 1 && isSurfaceOpen && pendingClaims.size === 0) startExchangeSession();
      }
      // A claimed card's reveal owns its charge and face from here. Anything
      // else — a refusal, a superseded pack — puts the card back as it was.
      const claimedHere = claimGeneration === openGeneration
        && activePack?.id === claimPackId
        && activePack?.items?.[index]?.claimed === true;
      if (!claimedHere && cardWrapper && !pendingRevealPreparations.get(cardWrapper)?.revealing) {
        discardPreparedPackFace(cardWrapper);
        setPackCardCharging(cardWrapper, false);
      }
      // An Exchange press may be waiting for the claims to settle.
      settleExchangeWaits();
    }
  }

  function setupEventListeners() {
    const section = getSection();
    if (!section || section.dataset.packsListeners === 'true') return;
    section.dataset.packsListeners = 'true';

    const buyBtn = document.getElementById('card-packs-buy-btn');
    if (buyBtn) bindGuarded(buyBtn, 'cardPacks.buyPack', handleBuyPack);

    const openBtn = document.getElementById('card-packs-open-btn');
    if (openBtn) bindGuarded(openBtn, 'cardPacks.openPack', handleOpenPack);

    const convertBtn = document.getElementById('card-packs-duplicates-btn');
    if (convertBtn) bindGuarded(convertBtn, 'cardPacks.convertDuplicates', handleConvertDuplicates);

    const seal = document.getElementById('card-packs-pack-seal');
    seal?.addEventListener('pointerdown', handleSealPointerDown);
    seal?.addEventListener('pointermove', handleSealPointerMove);
    seal?.addEventListener('pointerup', handleSealPointerEnd);
    seal?.addEventListener('pointercancel', handleSealPointerEnd);
    seal?.addEventListener('lostpointercapture', handleSealPointerEnd);
    seal?.addEventListener('click', handleSealClick);
    seal?.addEventListener('keydown', handleSealKeyDown);

    const pack = document.getElementById('card-packs-pack');
    pack?.addEventListener('pointerdown', handlePackPointerDown);
    pack?.addEventListener('pointermove', handlePackPointerMove);
    pack?.addEventListener('pointerup', handlePackPointerEnd);
    pack?.addEventListener('pointercancel', handlePackPointerEnd);
    pack?.addEventListener('lostpointercapture', handlePackPointerEnd);
    pack?.addEventListener('click', handlePackClick);

    const cardsContainer = document.getElementById('card-packs-cards');
    cardsContainer?.addEventListener('click', (event) => {
      const cardEl = event.target.closest('.card-pack-card');
      if (!cardEl) return;
      const index = Number(cardEl.dataset.index);
      if (!Number.isFinite(index)) return;
      handleClaimCard(index);
    });

    window.addEventListener('resize', scheduleSpreadUpdate);
    window.addEventListener('moonfall:convex-auth-changed', () => {
      if (isSurfaceOpen && typeof MoonfallCloud?.cardPacks?.getScreenState === 'function') void open();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && isSurfaceOpen && exchangeVersion === 1
        && !isConvertingDuplicates && !isOpening && !isLoading && pendingClaims.size === 0) void open();
    });
  }

  function setContext({
    allCards: nextCards = [],
    getLegacyCards: nextLegacyCards = null,
    ownedCardMap: nextOwnedMap,
    ownedCardStellarMap: nextOwnedStellarMap,
    currentUserId: nextUserId,
    stardust: nextStardust,
    selectedCardback: nextSelectedCardback,
    onStardustUpdate: nextStardustUpdate,
    onInventoryUpdate: nextInventoryUpdate,
    onInventoryInvalidated: nextInventoryInvalidated = null,
  } = {}) {
    const previousUserId = currentUserId;
    const resolvedUserId = nextUserId || null;
    const identityBecameKnown = previousUserId === null
      && resolvedUserId !== null;
    const identityChanged = previousUserId !== null
      && previousUserId !== resolvedUserId;
    if (identityBecameKnown || identityChanged) {
      stellarOddsKnown = false;
      duplicateTotalKnown = false;
      dropExchangeSession();
      packRefreshOwed = false;
      screenReadController?.abort();
      exchangeVersion = 0;
      openingCardVersion = 0;
      getLegacyCards = null;
      allCards = [];
      contextCards = [];
      onInventoryInvalidated = null;
    }
    if (identityBecameKnown) {
      // A boot-time anonymous read does not own the authenticated context that
      // Firebase just restored. Invalidate its generation immediately and
      // restart the read if the player still has Packs open. Otherwise the
      // late profile leaves that visible screen idle until another tab visit.
      // Keep a matching session tear record intact for reload recovery.
      contextGeneration += 1;
      openGeneration += 1;
      pendingRevealCues.clear();
      packStateEpoch += 1;
      packStateResolved = false;
      const section = getSection();
      if (['loading', 'refreshing'].includes(section?.dataset.packsLoadState)) {
        section.dataset.packsLoadState = 'idle';
      }
    } else if (identityChanged) {
      // This module survives sign-out/sign-in navigation. Its presentation
      // state does not: invalidate every old read/reveal, cancel physical
      // ceremony work, and empty the old account's vault before accepting the
      // new account's maps. Late mutation results are independently fenced by
      // contextGeneration.
      contextGeneration += 1;
      openGeneration += 1;
      pendingRevealCues.clear();
      packStateEpoch += 1;
      packStateResolved = false;
      // A read taken for the previous account is never painted for this one
      // (snapshotBelongsTo); stop paying for it too. A read that started while
      // the identity was still unknown is kept: its own account id decides.
      screenWarm?.controller.abort();
      screenWarm = null;
      resetSealDrag();
      clearTearPresentation();
      clearFoilShards();
      dismissPackPreview();
      activePack = null;
      packCount = 0;
      nonCraftableDuplicateValue = 0;
      locallyTornPackId = null;
      tearLeadInPending = false;
      isLoading = false;
      isOpening = false;
      isConvertingDuplicates = false;
      stardust = 0;
      onStardustUpdate = null;
      onInventoryUpdate = null;
      storeSessionTornPackId(null);
      setStatus('');
      const section = getSection();
      if (section) section.dataset.packsLoadState = 'idle';
    }

    if (typeof nextLegacyCards === 'function') getLegacyCards = nextLegacyCards;
    contextCards = openingCardVersion === 1 ? [] : (Array.isArray(nextCards) ? nextCards : []);
    if (openingCardVersion !== 1) allCards = contextCards;
    ownedCardMap = nextOwnedMap instanceof Map ? nextOwnedMap : new Map();
    ownedCardStellarMap = nextOwnedStellarMap instanceof Map ? nextOwnedStellarMap : new Map();
    currentUserId = resolvedUserId;
    if (sessionTornPackRecord?.userId === currentUserId) {
      locallyTornPackId = sessionTornPackRecord.packId;
    } else if (sessionTornPackRecord) {
      // A browser tab can sign out and into another account. Presentation
      // state from the previous identity must never cross that boundary.
      locallyTornPackId = null;
      storeSessionTornPackId(null);
    } else if (!currentUserId) {
      locallyTornPackId = null;
    }
    stardust = Number.isFinite(nextStardust) ? nextStardust : stardust;
    onStardustUpdate = nextStardustUpdate || onStardustUpdate;
    onInventoryUpdate = nextInventoryUpdate || onInventoryUpdate;
    onInventoryInvalidated = nextInventoryInvalidated || onInventoryInvalidated;
    const nextCardback = normalizeCardbackDescriptor(nextSelectedCardback);
    if (nextCardback.slug !== equippedCardback.slug
      || nextCardback.kind !== equippedCardback.kind
      || nextCardback.url !== equippedCardback.url) {
      stopCardbackObserver();
      setEquippedCardback(nextCardback);
    }
    ensureCardbackObserver();
    if (allCards.length > 0 && (ownedCardMap.size > 0 || ownedCardStellarMap.size > 0)) {
      updateDuplicateStateFromInventory();
    }
    prewarmKnownPackArt(activePack?.items || []);
    render();
    syncPackStateReadiness();
    if ((identityBecameKnown || (identityChanged && typeof MoonfallCloud?.cardPacks?.getScreenState === 'function')) && isSurfaceOpen) {
      // All new account fields are installed before the read starts. close()
      // revokes this ownership, so a delayed profile cannot reopen a departed
      // surface. Existing generation checks still reject the earlier response.
      void open();
    }
  }

  async function open() {
    const entering = !isSurfaceOpen;
    // A same-account re-read of a vault already on screen: the page coming
    // back into view, a token refresh, a language push, a reconciliation after
    // an action. Everything it shows stays as it is until the answer lands.
    const refreshing = !entering && packStateResolved;
    isSurfaceOpen = true;
    if (!refreshing) dropExchangeSession();
    screenReadController?.abort();
    screenReadController = new AbortController();
    ensureLayout();
    // Re-armed on every entry, not once at init: `setupEventListeners` runs
    // exactly one time and `close()` disconnects the observer, so a surface
    // opened a second time would otherwise be measuring nothing.
    observeFanGeometry();
    ensureCardbackObserver();
    setupEventListeners();
    // The pack ceremony's cues sit late in the global manifest and used to be
    // cold on first use. Re-prioritise both element voices and decoded buffers
    // on surface entry without delaying the state read or accepting an action.
    void SoundEffects?.preload?.([
      'impact',
      'pack_open',
      'card_reveal',
      'stellar',
      'press',
      'magic',
    ]);
    // The wrapper the ceremony reveals is 360-610 KB and its identity is only
    // known at charge time. Warm every candidate now so the reveal is a swap,
    // not a download (docs/asset-loading.md law 3: best-effort, non-blocking).
    warmAllPackWrappers();
    const section = getSection();
    const generation = ++openGeneration;
    const stateEpoch = packStateEpoch;
    const userId = currentUserId;
    // A read started on intent (warm) is this entry's read; it is used once.
    const screenStateApi = typeof MoonfallCloud?.cardPacks?.getScreenState === 'function';
    const warmed = screenStateApi ? usableWarm() : null;
    /* AN ENTRY prepare() ALREADY PAINTED TAKES NOTHING BACK. The Collection
       calls prepare() before it shows the section and open() in the same
       task; with the warm read in hand prepare() has painted the finished
       vault from it, current terms included, and open() commits that same
       read again. Taking the entry hold and the loading state for those few
       microtasks hid the header actions and disabled Buy and Charge, and a
       style pass in between turned their return into transitions: both
       buttons faded in ~110 ms behind the finished vault (snappy round 15,
       `tab-packs`, Chrome desktop, every visit). */
    const preparedEntry = entering && warmed !== null && warmed === preparedWarm
      && snapshotBelongsTo(warmed.snapshot, userId);
    preparedWarm = null;
    if (refreshing || preparedEntry) {
      /* Found 25 September 2026 (flicker hunt): a refresh cleared the
         disclosure and set the vault loading, so coming back to the tab blanked
         the published odds (the header lost a line and the stage jumped) and
         greyed Buy, Charge and Exchange until the read landed. What it keeps is
         this account's, from the server. An action taken meanwhile is fenced by
         packStateEpoch (the read is then superseded, below), and a read or
         terms that fail still withdraw the disclosure and close the till. */
      if (section && refreshing) section.dataset.packsLoadState = 'refreshing';
    } else {
      // Every entry needs a current disclosure, including warm revisits whose
      // config request may fail. Clear the previous account/debug rate before
      // any asynchronous read or language event can render it again — out of
      // sight on an entry (beginEntryHold), so the header never visibly
      // collapses and regrows around the read.
      if (entering) beginEntryHold(section);
      stellarOddsKnown = false;
      packConfig.stellarPackOdds = undefined;
      if (section) section.dataset.packsLoadState = 'loading';
      syncPackStateReadiness();
      // Disable state-changing controls in the same task that starts the read;
      // otherwise the prior ready paint leaves a charge/buy button actionable
      // until the response lands.
      updateSummary();
    }
    if (warmed) warmed.consumed = true;
    // Independent reads start together (docs/asset-loading.md law 4).
    // A refresh leaves a finished pack the player is looking at on the stage
    // (finishedPackOnStage); an entry starts from the vault the server lists.
    const keepFinished = refreshing;
    const regionKnown = readPackRegion();
    const stateLoaded = screenStateApi
      ? await loadScreenState(generation, stateEpoch, userId, warmed, { keepFinished })
      : (await Promise.all([loadConfig(generation), loadPackState(generation, stateEpoch, userId, undefined, { keepFinished }),
        loadLegacyCardContext(generation, userId)]))[1];
    // Normally answered long before; the vault waits for it rather than
    // offering a pack and then withdrawing it.
    await regionKnown;
    if (generation !== openGeneration || userId !== currentUserId) return;
    if (refreshing && !stateLoaded && stateEpoch !== packStateEpoch) {
      // Overtaken by the player's own action (a purchase, a charge, a claim)
      // or by a running exchange moving packs: the read describes the vault
      // from before, and what overtook it has painted its own answer. Keep
      // that; read again once it has settled.
      if (section) section.dataset.packsLoadState = 'ready';
      packRefreshOwed = true;
      readOwedRefresh();
      return;
    }
    if (stateLoaded) packStateResolved = true;
    else if (typeof MoonfallCloud?.cardPacks?.getScreenState === 'function') packStateResolved = false;
    if (refreshing && !packStateResolved) {
      // A refresh that failed withdraws what it kept, as a failed entry would.
      stellarOddsKnown = false;
      packConfig.stellarPackOdds = undefined;
      dropExchangeSession();
    }
    if (section) {
      section.dataset.packsLoadState = packStateResolved ? 'ready' : 'error';
    }
    // Commit the authoritative classes/cards before revealing the cold stage;
    // both changes land in one task, so no intermediate sealed wrapper paints.
    render();
    syncPackStateReadiness();
    if (section && 'packsEntering' in section.dataset) delete section.dataset.packsEntering;
    if (stateLoaded) startExchangeSession();
  }

  function close() {
    isSurfaceOpen = false;
    dropExchangeSession();
    packRefreshOwed = false;
    screenReadController?.abort(); screenReadController = null;
    /* The opening's card descriptions stay: a closed surface keeps describing
       its own pack, so the Collection's context refresh on the way back finds
       every revealed face unchanged instead of re-describing it as unknown
       (disposing its settled paint) until the read lands. An account change
       clears them (setContext), and every read replaces them. */
    isConvertingDuplicates = false;
    openGeneration += 1;
    pendingRevealCues.clear();
    stopCardbackObserver();
    dismissPackPreview();
    const section = getSection();
    const clearBackRaster = CardBackRaster?.clear;
    if (section && typeof clearBackRaster === 'function') {
      section.querySelectorAll('.card-pack-card-back .card-back').forEach((back) => {
        clearBackRaster(back);
      });
    }
    if (section) {
      section.dataset.packsLoadState = 'idle';
      delete section.dataset.packsEntering;
      /* THE FAN THE PLAYER SAW STAYS DEALT. The section is display:none until
         the Packs tab comes back, and showing it restarts every CSS animation
         in it: the deal (the only thing that makes a fan card visible) and a
         revealed card's aura and landing would all play again. Shift the
         timelines already seen to their end, where their fills hold the rest
         frame. A new pack builds new wrappers and deals normally; a card
         revealed after the return has no reveal shift and plays in full. */
      section.querySelectorAll('.card-pack-card').forEach((node) => {
        const cardWrapper = /** @type {HTMLElement} */ (node);
        cardWrapper.style.setProperty('--card-delay', '-60s');
        if (cardWrapper.classList.contains('card-pack-card-revealed')) {
          cardWrapper.style.setProperty('--card-reveal-shift', '-60s');
        }
      });
    }
    setStatus('');
    resetSealDrag();
    clearTearPresentation();
    if (spreadRaf) {
      cancelAnimationFrame(spreadRaf);
      spreadRaf = null;
    }
    fanGeometryObserver?.disconnect();
    clearFoilShards();
  }

  function setupI18nListeners() {
    if (i18nListenersReady) return;
    i18nListenersReady = true;
    window.addEventListener('moonfall:i18nready', render);
    window.addEventListener('moonfall:languagechange', () => {
      // The new copy at once: the published figures do not depend on the
      // language. Only the opening's card descriptions do, and they arrive
      // with the re-read, which keeps everything on screen until it lands.
      render();
      if (isSurfaceOpen && openingCardVersion === 1) void open();
    });
  }

  // Pointing at (or focusing, or touching) the Packs tab is the intent that
  // precedes the click by a few hundred milliseconds: start its read there.
  function watchPacksTabIntent() {
    const tab = document.getElementById('collection-tab-packs');
    if (!tab || tab.dataset.packsWarmIntent === 'true') return;
    tab.dataset.packsWarmIntent = 'true';
    const intent = () => { void warm(); };
    ['pointerenter', 'pointerdown', 'focus'].forEach((type) => {
      tab.addEventListener(type, intent, { passive: true });
    });
  }

  function init() {
    ensureLayout();
    setupEventListeners();
    setupI18nListeners();
    watchPacksTabIntent();
    void readPackRegion();
  }

  return {
    init,
    open,
    close,
    setContext,
    warm,
    prepare,
  };
})();

export { CardPacks };
